import React, { useState, useEffect, useRef, useCallback } from "react";
// import './bootstrapApiBase'; // removed to match older stable builds
// bootstrapApiBase removed to match older stable builds


import "./App.css";
import axios from "axios";
import LandingPage from "./LandingPage";
import LoginPage from "./LoginPage";
import EmailVerificationPage from "./EmailVerificationPage";
import ResetPasswordPage from "./ResetPasswordPage";
import SettingsPage from "./SettingsPage";
import SubscriptionPage from "./SubscriptionPage";
import AdminDashboard from "./AdminDashboard";
import TermsOfService from "./TermsOfService";
import HowToStartPage from "./HowToStartPage";
import PrivacyPolicy from "./PrivacyPolicy";
import RiskDisclaimer from "./RiskDisclaimer";
import CookiePolicy from "./CookiePolicy";
import LegalFooter from "./LegalFooter";
import ContactSupport from "./ContactSupport";
import CookiesNotification from "./CookiesNotification";

// Dashboard Components
import AnnouncementBanner from "./components/AnnouncementBanner";
import PortfolioSummary from "./components/PortfolioSummary";
import TradingControls from "./components/TradingControls";
import MarketStatus from "./components/MarketStatus";
import ConfirmModal from "./components/ConfirmModal";
import DashboardHeader from "./components/DashboardHeader";
import HealthCheckBanner from "./components/HealthCheckBanner";
import { ErrorToast, SuccessToast } from "./components/ToastNotification";
import GridBotDashboard from "./components/GridBotDashboard";
import ControlPanel from "./components/ControlPanel";
import PositionsTable from "./components/PositionsTable";
import AdminTradingPanel from "./components/AdminTradingPanel";
import DashboardNavigation from "./components/DashboardNavigation";
import { formatCurrency as formatCurrencyUtil, formatPercentage, formatTimestamp, formatCooldownTimer, getSignalColor, getMarketStatusColor } from "./utils/formatters";
import { SOLID100, companyNames } from "./utils/constants";

// Resolve backend base URL safely at runtime:
// Use window.location.origin for robust deployment (works with proxy/ingress)
// This avoids stale REACT_APP_BACKEND_URL issues
const BACKEND_URL = window.location.origin;
// const ENV_URL = process.env.REACT_APP_BACKEND_URL;
// const BACKEND_URL = ENV_URL || RUNTIME_ORIGIN;

// noisy startup logs removed

const API = `${BACKEND_URL}/api`;
const API_FALLBACK = `/api`;

// Configure axios to handle 502 errors with automatic retry after tab wake-up
axios.interceptors.response.use(
  response => response,
  async error => {
    const config = error.config;

    // ── Centralized error log (Feb 2026) ────────────────────
    // Every failed API call lands here. Print rich context to the
    // browser console so the user can see all faults/errors at a glance
    // without us cluttering the UI with banners.
    try {
      const url = config?.url || '(unknown url)';
      const method = (config?.method || 'GET').toUpperCase();
      const status = error.response?.status;
      const body = error.response?.data;
      const code = error.code;
      const isNet = !error.response;
      const tag = isNet ? '%c[NET]' : (status >= 500 ? '%c[5xx]' : (status === 429 ? '%c[429]' : '%c[4xx]'));
      const color = isNet ? 'color:#f59e0b' : (status >= 500 ? 'color:#ef4444' : (status === 429 ? 'color:#fb923c' : 'color:#facc15'));
      // eslint-disable-next-line no-console
      console.warn(
        `${tag} ${method} ${url} → ${status || code || 'no-response'}`,
        color,
        { body, code, message: error.message, headers: error.response?.headers }
      );
    } catch (_) { /* never block real error flow on logging */ }

    // Handle 502 Bad Gateway errors (common after tab wake-up or backend cold start)
    if (error.response?.status === 502 && !config._retry) {
      config._retry = true;
      config._retryCount = (config._retryCount || 0) + 1;
      
      // Retry up to 2 times with exponential backoff
      if (config._retryCount <= 2) {
        const delay = Math.min(1000 * Math.pow(2, config._retryCount - 1), 3000);
        console.log(`⚠️ 502 error on ${config.url}, retrying in ${delay}ms (attempt ${config._retryCount}/2)...`);
        
        await new Promise(resolve => setTimeout(resolve, delay));
        return axios(config);
      }
    }
    
    return Promise.reject(error);
  }
);

// Global uncaught error sinks → console (no UI noise).
if (typeof window !== 'undefined') {
  window.addEventListener('error', (ev) => {
    // eslint-disable-next-line no-console
    console.error('%c[UNCAUGHT]', 'color:#ef4444', ev.message, {
      filename: ev.filename, line: ev.lineno, col: ev.colno, error: ev.error,
    });
  });
  window.addEventListener('unhandledrejection', (ev) => {
    // eslint-disable-next-line no-console
    console.error('%c[UNHANDLED-PROMISE]', 'color:#ef4444', ev.reason);
  });
}

// Helper used by app code to emit structured bot/state logs.
// Available globally as window.__botLog so any module can call it
// without a dedicated import.
if (typeof window !== 'undefined') {
  window.__botLog = (tag, ...args) => {
    try {
      // eslint-disable-next-line no-console
      console.log(`%c[BOT/${tag}]`, 'color:#60a5fa', ...args);
    } catch (_) { /* swallow */ }
  };
}

const TradingBot = () => {
  // Helper to format the "Tracking Top X" text
  const getTrackingTitle = () => {
    // Keep header stable and human-readable (do NOT print addresses here)
    return "Tracking Top 5 Elite Traders";
  };

  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [initialLoginMode, setInitialLoginMode] = useState(true); // true = login, false = signup
  const [currentUser, setCurrentUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  
  // Check if running in iframe (preview window)
  const [isInIframe, setIsInIframe] = useState(false);
  
  useEffect(() => {
    // Detect if running in iframe
    const inIframe = window.self !== window.top;
    setIsInIframe(inIframe);
    
    if (inIframe) {
      console.log('🖼️ Running in iframe context (preview window)');
      // Add iframe-specific handling
      document.body.style.margin = '0';
      document.body.style.padding = '0';
    } else {
      // noisy startup logs removed
    }
  }, []);
  
  // Password reset state
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [resetToken, setResetToken] = useState(null);
  const [resetMessage, setResetMessage] = useState('');
  
  // Settings page state
  const [showSettings, setShowSettings] = useState(false);
  // How to Start page state
  const [showHowToStart, setShowHowToStart] = useState(false);
  
  // Subscription page state
  const [showSubscription, setShowSubscription] = useState(false);
  
  // Admin dashboard state  
  const [showAdmin, setShowAdmin] = useState(false);
  
  // Legal page states
  const [showLegalPage, setShowLegalPage] = useState(null); // 'terms', 'privacy', 'risk', 'cookies'
  
  // Email verification state
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  
  // Subscription status
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  
  // Live clock that updates every second
  const [currentTime, setCurrentTime] = useState(new Date());
  
  // Autonomous trading state
  const [autonomousStatus, setAutonomousStatus] = useState({
    autonomous_mode: false,
    is_running: false,
    user_participating: false,
    paper_trading: true,
    trades_executed: 0,
    session_duration_hours: 0,
    engine_state: 'stopped',
    cooldown_active: false,
    cooldown_seconds_remaining: 0,
    hydra_regime: null,
    non_ranging_entry_pause_active: false,
    regime_filter_pause_active: false,
    regime_filter_disallowed_regime: null,
  });
  const [autonomousLoading, setAutonomousLoading] = useState(false);
  const [cooldownRemainingSec, setCooldownRemainingSec] = useState(0);
  const [allowedTradeRegimes, setAllowedTradeRegimes] = useState(['ranging']);
  const [allowedTradeRegimesLoading, setAllowedTradeRegimesLoading] = useState(false);
  const [tradingNotional, setTradingNotional] = useState(1);
  const [tradingLeverage, setTradingLeverage] = useState(10);
  const [tradingCoin, setTradingCoin] = useState('BTC');
  const [autoRotateCoin, setAutoRotateCoin] = useState(false);
  const [autoRotateLoading, setAutoRotateLoading] = useState(false);
  const [cutAndTrail, setCutAndTrail] = useState(false);
  const [cutAndTrailLoading, setCutAndTrailLoading] = useState(false);
  const [ctAdaptive, setCtAdaptive] = useState(false);
  const [ctAdaptiveLoading, setCtAdaptiveLoading] = useState(false);
  const [hybridExit, setHybridExit] = useState(false);
  const [hybridExitLoading, setHybridExitLoading] = useState(false);
  const [supportedCoins, setSupportedCoins] = useState(['BTC', 'ETH', 'SOL', 'BNB', 'XRP', 'HYPE', 'DOGE', 'ADA', 'AVAX', 'LINK']);
  const [maxLeverageByCoin, setMaxLeverageByCoin] = useState({
    BTC: 40, ETH: 25, SOL: 20, BNB: 10, XRP: 20,
    HYPE: 10, DOGE: 10, ADA: 10, AVAX: 10, LINK: 10,
  });
  
  // Initialize portfolio with proper structure that shows loading state
  const [portfolio, setPortfolio] = useState({
    cash: null,
    total_value: null, 
    positions: [],
    daily_pnl: null,
    total_pnl: null,
    currency: 'USD',
    loading: true,
    subscriptionRequired: false,
    apiKeyRequired: false,
    lastUpdated: null,  // Timestamp of last successful fetch
    isStale: false      // True if last refresh failed
  });
  // Keep last TA to avoid spinner flicker on refresh
  const lastTARef = useRef({});

  // Backoff mechanism for bulk market data fetching
  const bulkBackoffRef = useRef({ until: 0, factor: 1 });
  
  // In-flight request tracking to prevent duplicate API calls
  const inFlightRef = useRef({
    tradingStats: false,
    autonomousStatus: false,
    portfolio: false
  });

  const [trades, setTrades] = useState([]);
  const [marketData, setMarketData] = useState({});
  // Public Crypto Market Status bootstrap for first paint (works without auth)
  // Bootstrap polling moved to Master Poller (search for "Master Poller" above)

  const [technicalAnalysis, setTechnicalAnalysis] = useState({});
  const [technicalAnalysisLoading, setTechnicalAnalysisLoading] = useState(true);
  const [aiThoughts, setAiThoughts] = useState({ reasoning: '', score: {} });
  const [showAiThoughts, setShowAiThoughts] = useState(false);
  const [tradingStatus, setTradingStatus] = useState({
    active: true,
    market_status: {
      mode: "OPEN",
      strategy: "grid_bot",
      us_open: true,
      us_time: new Date().toLocaleTimeString(),
      market_hours: { is_open: true }
    }
  });
  const [tradingStats, setTradingStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState(null);  // Non-blocking error for actions (start/stop)
  const [successMessage, setSuccessMessage] = useState(null);
  const [showStopConfirm, setShowStopConfirm] = useState(false);  // For live mode stop confirmation
  const [showCloseAllConfirm, setShowCloseAllConfirm] = useState(false);  // For close all positions confirmation
  const [selectedSymbol, setSelectedSymbol] = useState('BTC-USD');
  // Disaster recovery removed
  
  // Backend health check state
  // Stripe payment return status banner
  const [paymentStatusBanner, setPaymentStatusBanner] = useState('');

  const [backendHealthy, setBackendHealthy] = useState(true);
  const [healthCheckMessage, setHealthCheckMessage] = useState('');
  const [showHealthBanner, setShowHealthBanner] = useState(false);
  
  // Backend health check function with enhanced error detection
  const checkBackendHealth = async () => {
    try {
      const ctrl = new AbortController();
      const t = setTimeout(() => ctrl.abort(), 2000); // 2s max
      const liteResp = await fetch(`${API}/health-lite`, { method: 'GET', signal: ctrl.signal });
      clearTimeout(t);

      if (liteResp.ok) {
        setBackendHealthy(true);
        setShowHealthBanner(false);
        return true;
      }
      // Non-200 but no exception
      setBackendHealthy(false);
      setHealthCheckMessage('Backend health-lite returned non-200');
      // Debounce banner: only show after 2 consecutive failures
      setShowHealthBanner(prev => prev ? true : false);
      return false;
    } catch (error) {
      // Debounce banner: require 2 consecutive failures to display
      setBackendHealthy(false);
      setShowHealthBanner(prev => prev ? true : false);
      setHealthCheckMessage(`CONNECTIVITY ERROR: ${error.message}`);
      return false;
    }
  };

  // Check authentication on component mount
  useEffect(() => {
    // Set up global error handler to catch PostHog errors before they interfere
    const originalConsoleError = console.error;
    console.error = function(...args) {
      // Filter out PostHog-related errors from appearing as critical errors
      const errorMessage = args.join(' ');
      if (errorMessage.includes('posthog') || 
          errorMessage.includes('WebSocket') || 
          errorMessage.includes('ws://') ||
          errorMessage.includes('wss://') ||
          errorMessage.includes('phc_yJW1VjHGGwmCbbrtczfqqNxgBDbhlhOWcdzcIJEOTFE') ||
          errorMessage.includes('Extension context invalidated') ||
          errorMessage.includes('message channel closed before a response was received') ||
          errorMessage.includes('A listener indicated an asynchronous response by returning true')) {
        // Log known browser/extension noise as warnings instead
        console.warn('⚠️ [Non-Critical Client Noise]:', ...args);
        return;
      }
      // Let other errors through normally
      originalConsoleError.apply(console, args);
    };
    
    const checkAuth = async () => {
      const user = localStorage.getItem('user');
      
      // Check for email verification token in URL
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get('token');

      // Handle Stripe return routes by forcing Subscription page open for polling.
      // Stripe redirects to /subscription/success?session_id=... (no `payment`
      // param), and the cancel URL is /subscription/cancel. We trigger on
      // EITHER the success session_id OR an explicit ?payment=... param OR a
      // /subscription/success|cancel path so the user always lands on the
      // subscription screen rather than the marketing landing page.
      const paymentParam = urlParams.get('payment');
      const sessionIdParam = urlParams.get('session_id');
      const onSubscriptionPath = window.location.pathname.startsWith('/subscription');
      const onStripeReturnPath = (
        window.location.pathname.startsWith('/subscription/success') ||
        window.location.pathname.startsWith('/subscription/cancel')
      );
      if (
        onStripeReturnPath ||
        (onSubscriptionPath && (paymentParam || sessionIdParam))
      ) {
        setShowSubscription(true);
      }
      
      if (token && window.location.pathname.includes('/verify-email')) {
        setShowEmailVerification(true);
        setAuthLoading(false);
        return;
      }
      
      // Check for password reset token in URL
      if (token && window.location.pathname.includes('/reset-password')) {
        setResetToken(token);
        setShowPasswordReset(true);
        setAuthLoading(false);
        return;
      }
      
      if (user) {
        try {
          const userData = JSON.parse(user);
          // Privacy: do not log localStorage user data
          setCurrentUser(userData);
          setIsAuthenticated(true);
          
          // Auto-detect admin users and automatically show admin dashboard
          // Also set is_admin flag on currentUser object for proper rendering
          if (userData) {
            // Privacy: do not log user/admin status payloads
            
            const isAdmin = userData.is_admin || userData.role === 'super_admin' || userData.admin_role === 'super_admin';
            userData.is_admin = isAdmin; // Ensure flag is set on object
            setCurrentUser(userData); // Update state with flag
            
            // Automatically show admin dashboard for admin users
            if (isAdmin) {
              setShowAdmin(true);
            }
          }
          
          // Check subscription status for authenticated users
          checkSubscriptionStatus();
        } catch (e) {
          // Invalid user data, clear storage
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        }
      }
      
      setAuthLoading(false);
    };
    
    checkAuth();
    
    // Run backend health check

    checkBackendHealth();
    
    // Check for admin access via URL
    checkAdminAccess();
    
    // Check for legal page routing
    checkLegalPageRouting();
    
    // Sync showLegalPage state when browser navigates (back/forward buttons)
    const handlePopState = () => {
      const path = window.location.pathname;
      if (path === '/' || path === '') {
        setShowLegalPage(null);
      } else {
        checkLegalPageRouting();
      }
    };
    window.addEventListener('popstate', handlePopState);
    
    // Add keyboard shortcut for admin access (Ctrl+Shift+A)
    const handleKeyPress = (e) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'A') {
        e.preventDefault();
        setShowAdmin(true);
      }
    };
    
    document.addEventListener('keydown', handleKeyPress);
    
    // Set up unhandled error catching to prevent PostHog from crashing the app
    const handleUnhandledError = (event) => {
      const error = event.error || event.reason || event;
      const errorMessage = (error && typeof error === 'object')
        ? (error.message || String(error))
        : String(error || '');

      if (errorMessage.includes('posthog') || 
          errorMessage.includes('WebSocket') || 
          errorMessage.includes('phc_yJW1VjHGGwmCbbrtczfqqNxgBDbhlhOWcdzcIJEOTFE') ||
          errorMessage.includes('Extension context invalidated') ||
          errorMessage.includes('message channel closed before a response was received') ||
          errorMessage.includes('A listener indicated an asynchronous response by returning true')) {
        console.warn('⚠️ [Non-Critical Client Error Caught]:', errorMessage);
        event.preventDefault();
        return false;
      }
    };
    
    window.addEventListener('error', handleUnhandledError);
    window.addEventListener('unhandledrejection', handleUnhandledError);
    
    // Add visibility change listener to aggressively stop polling when tab hidden
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // noisy polling log removed
      } else {
        console.log('👁️ Tab visible - polling will resume');
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
      window.removeEventListener('error', handleUnhandledError);
      window.removeEventListener('unhandledrejection', handleUnhandledError);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('popstate', handlePopState);
      // Restore original console.error
      console.error = originalConsoleError;
    };
  }, []);

  // Auto-dismiss success messages after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [successMessage]);
  const checkAdminAccess = () => {
    // Check if URL contains admin access parameter
    const urlParams = new URLSearchParams(window.location.search);
    const adminAccess = urlParams.get('admin');
    const currentPath = window.location.pathname;
    
    // Trigger admin dashboard if special parameter or path is detected
    if (adminAccess === 'true' || currentPath.includes('/admin-access') || currentPath.includes('/backdoor')) {
      setShowAdmin(true);
      // Clean the URL to hide the admin access
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  };

  const checkLegalPageRouting = () => {
    // Check for legal page routes
    const currentPath = window.location.pathname;
    const urlParams = new URLSearchParams(window.location.search);
    
    if (currentPath === '/terms' || currentPath === '/terms-of-service') {
      setShowLegalPage('terms');
    } else if (currentPath === '/privacy' || currentPath === '/privacy-policy') {
      setShowLegalPage('privacy');
    } else if (currentPath === '/risk' || currentPath === '/risk-disclaimer') {
      setShowLegalPage('risk');
    } else if (currentPath === '/cookies' || currentPath === '/cookie-policy') {
      setShowLegalPage('cookies');
    } else if (currentPath === '/support' || currentPath === '/contact') {
      const supportType = urlParams.get('type');
      setShowLegalPage(supportType ? `support-${supportType}` : 'support');
    }
  };

  // Helper: build 200-symbol universe flattened from categories
  const buildLiveSymbols = () => {
    try {
      const map = allSymbols || {};
      const out = [];
      Object.keys(map).forEach((k) => {
        (map[k] || []).forEach((s) => { if (typeof s === 'string') out.push(s.toUpperCase()); });
      });
      // de-dup preserve order
      const seen = new Set();
      const uniq = [];
      for (const s of out) { if (!seen.has(s)) { seen.add(s); uniq.push(s); } }
      return uniq;
    } catch { return []; }
  };

  // Lightweight shard status
  const [shardStatus, setShardStatus] = useState(null);
  const ensureIngestionOnce = useRef(false);
  const ensureIngestion = async () => {
    // Legacy ingestion endpoints removed — crypto grid bot doesn't need them
    return;
  };

  // Restore subscription checking - only popup display is disabled
  const checkSubscriptionStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setSubscriptionLoading(false);
        return;
      }

      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'timeout': 20000
      };

      let response;
      try {
        response = await axios.get(`${API}/user/subscription`, { headers });
      } catch (primaryErr) {
        console.warn('Primary load subscription failed, trying fallback /api ...', primaryErr?.message || primaryErr);
        try {
          response = await axios.get(`${API_FALLBACK}/user/subscription`, { headers });
        } catch (fallbackErr) {
          console.warn('Fallback /api subscription failed:', fallbackErr?.message || fallbackErr);
          setSubscriptionLoading(false);
          return;
        }
      }
      
      if (response && response.data) {
        setSubscriptionStatus(response.data);
      }
      
      setSubscriptionLoading(false);
    } catch (error) {
      console.error('Error checking subscription:', error);
      setSubscriptionLoading(false);
    }
  };

  const handleLogin = (authData) => {
    // Privacy: do not log auth data
    
    // The LoginPage already stores data in localStorage, 
    // we just need to set the state correctly
    setCurrentUser(authData.user);
    setIsAuthenticated(true);
    setShowLogin(false);
    
    // FIX 162: Reset portfolio to loading state for clean slate
    setPortfolio({
      cash: null, total_value: null, positions: [], daily_pnl: null,
      total_pnl: null, currency: 'USD', loading: true,
      subscriptionRequired: false, apiKeyRequired: false,
      lastUpdated: null, isStale: false
    });
    setTradingStats(null);
    // FIX 162: Reset throttle so fresh fetch happens immediately
    lastPortfolioRefreshRef.current = 0;
    
    console.log('Authentication state set to true');

    // Auto-detect admin users and set admin dashboard state
    if (authData.user) {
      // Privacy: do not log user/admin status payloads
      const isAdmin = authData.user.is_admin || authData.user.role === 'super_admin' || authData.user.admin_role === 'super_admin';
      if (isAdmin) {
        setShowAdmin(true);
      }
    }

    // Ramp bulk fetch size from 20 to 100 after 10s to prevent login stalls but scale quickly
    setBulkBatchSize(20);
    setTimeout(() => setBulkBatchSize(100), 10000);

    // Check subscription status after login
    checkSubscriptionStatus();
  };

  // Enhanced logout with per-user cache cleanup
  const handleLogout = async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const userId = user.user_id;
      
      // OPTIMIZED: Call backend logout but don't wait for it (fire and forget)
      if (userId) {
        const headers = {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        };
        
        // Fire-and-forget: don't await, logout continues immediately
        // Increased timeout to 10s to handle slow backend responses
        axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { headers, timeout: 10000 })
          .then(res => console.log(`🧹 Server-side cache cleanup for user ${userId}:`, res.data))
          .catch(err => {
            // Silently ignore timeout errors on logout - user is already logged out locally
            if (err?.code !== 'ECONNABORTED') {
              console.warn('Server logout cleanup failed:', err?.message || err);
            }
          });
      }
      
      // Clear localStorage but preserve cookie consent preference
      const cookiesAccepted = localStorage.getItem('cookiesAccepted');
      localStorage.clear();
      if (cookiesAccepted !== null) {
        localStorage.setItem('cookiesAccepted', cookiesAccepted);
      }
      console.log(`🧹 Client-side cleanup: cleared localStorage (preserved cookie consent)`);
      
      // FIX 162: Reset ALL user-specific state to prevent cross-account contamination
      setIsAuthenticated(false);
      setCurrentUser(null);
      setPortfolio({
        cash: null, total_value: null, positions: [], daily_pnl: null,
        total_pnl: null, currency: 'USD', loading: true,
        subscriptionRequired: false, apiKeyRequired: false,
        lastUpdated: null, isStale: false
      });
      setTradingStatus({
        active: true,
        market_status: {
          mode: "OPEN", strategy: "grid_bot", us_open: true,
          us_time: new Date().toLocaleTimeString(),
          market_hours: { is_open: true }
        }
      });
      setTradingStats(null);
      setMarketData({});
      setTechnicalAnalysis({});
      // FIX 162: Reset autonomous status to prevent showing previous user's bot state
      setAutonomousStatus({
        autonomous_mode: false, is_running: false, user_participating: false,
        paper_trading: true, trades_executed: 0, session_duration_hours: 0,
        engine_state: 'stopped', cooldown_active: false,
        cooldown_seconds_remaining: 0, hydra_regime: null,
        non_ranging_entry_pause_active: false, regime_filter_pause_active: false,
        regime_filter_disallowed_regime: null,
      });
      setCooldownRemainingSec(0);
      setError(null);
      setActionError(null);
      setSuccessMessage(null);
      setLoading(false);
      setShowLogin(false);
      setInitialLoginMode(true); // Reset to login mode
      setShowSettings(false);
      setShowSubscription(false);
      setShowAdmin(false);
      setSubscriptionStatus(null);
      // FIX 162: Reset throttle ref so next user gets fresh fetch immediately
      lastPortfolioRefreshRef.current = 0;
      
      console.log('✅ User logged out successfully');
      
    } catch (error) {
      console.error('Logout error:', error);
      // Force cleanup even if there were errors (preserve cookie consent)
      const cookiesAccepted = localStorage.getItem('cookiesAccepted');
      localStorage.clear();
      if (cookiesAccepted !== null) {
        localStorage.setItem('cookiesAccepted', cookiesAccepted);
      }
      setIsAuthenticated(false);
      setCurrentUser(null);
      setPortfolio({
        cash: null, total_value: null, positions: [], daily_pnl: null,
        total_pnl: null, currency: 'USD', loading: true,
        subscriptionRequired: false, apiKeyRequired: false,
        lastUpdated: null, isStale: false
      });
      setTradingStats(null);
      setAutonomousStatus({
        autonomous_mode: false, is_running: false, user_participating: false,
        paper_trading: true, trades_executed: 0, session_duration_hours: 0,
        engine_state: 'stopped', cooldown_active: false,
        cooldown_seconds_remaining: 0, hydra_regime: null,
        non_ranging_entry_pause_active: false, regime_filter_pause_active: false,
        regime_filter_disallowed_regime: null,
      });
      lastPortfolioRefreshRef.current = 0;
    }
  };

  // Password reset handlers
  const handleResetSuccess = (message) => {
    setResetMessage(message);
    setShowPasswordReset(false);
    setResetToken(null);
    // Clear URL parameters
    window.history.replaceState({}, document.title, window.location.pathname);
  };

  const handleResetBack = () => {
    setShowPasswordReset(false);
    setResetToken(null);
    setResetMessage('');
    // Clear URL parameters
    window.history.replaceState({}, document.title, window.location.pathname);
  };

  // Configure axios to include auth token
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [isAuthenticated]);

  // Clear any hash fragment (e.g. #how-it-works from landing-page anchor clicks)
  // when the user logs in, so it doesn't stay stuck in the URL across views.
  useEffect(() => {
    if (isAuthenticated && window.location.hash) {
      try {
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
      } catch {
        // best-effort; ignore if browser blocks history API
      }
    }
  }, [isAuthenticated]);

  // Simplified active symbols management (restrict to validated live symbols)
  useEffect(() => {
    const initial = Object.values(allSymbols).flat();
    setActiveSymbols(initial);
    const initialMarketData = {};
    initial.forEach(symbol => { initialMarketData[symbol] = { loading: true, error: false }; });
    setMarketData(initialMarketData);
    if (!selectedSymbol) {
      setSelectedSymbol(initial.includes('BTC-USD') ? 'BTC-USD' : initial[0]);
    }
  }, []); // Only run once on mount

  // Ensure market data map has placeholders for all Live symbols and set active list
  useEffect(() => {
    const list = Object.values(allSymbols).flat();
    const initial = {};
    list.forEach(s => { if (!marketData[s]) initial[s] = { loading: true, error: false }; });
    if (Object.keys(initial).length > 0) setMarketData(prev => ({ ...initial, ...prev }));
    setActiveSymbols(list);
  }, []);
  


  // Dynamic symbols based on market hours
  const [activeSymbols, setActiveSymbols] = useState([]);
  
  // Enhanced dashboard state management
  const [selectedCategory, setSelectedCategory] = useState('Live');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('category'); // 'category', 'favorites', 'search'
  const [favoriteSymbols, setFavoriteSymbols] = useState(['BTC-USD', 'ETH-USD', 'SOL-USD']);
  // Visible-only low-impact fetching helpers
  const listRef = useRef(null);
  const [visibleSymbols, setVisibleSymbols] = useState([]);
  const [pagePaused, setPagePaused] = useState(false);

  // Build 1D array of current UI list based on filters
  const buildCurrentUiList = () => {
    try { return getActiveSymbols(); } catch { return []; }
  };

  // Track visible symbols in the scroll container to minimize fetches
  useEffect(() => {
    const el = listRef.current;
    const compute = () => {
      try {
        const list = buildCurrentUiList();
        if (!el || !list || list.length === 0) { setVisibleSymbols([]); return; }
        const itemHeight = 72; // approximate card height
        const scrollTop = el.scrollTop || 0;
        const viewH = el.clientHeight || 0;
        const start = Math.max(0, Math.floor(scrollTop / itemHeight) - 1);
        const end = Math.min(list.length, start + Math.ceil(viewH / itemHeight) + 3);
        const slice = list.slice(start, end);
        setVisibleSymbols(slice);
      } catch (_) { /* no-op */ }
    };
    compute();
    if (el) el.addEventListener('scroll', compute);
    window.addEventListener('resize', compute);
    return () => { if (el) el.removeEventListener('scroll', compute); window.removeEventListener('resize', compute); };
  }, [selectedCategory, viewMode, searchQuery]);

  // Pause polling when tab is hidden and handle reconnection on visibility change
  useEffect(() => {
    // Cooldown on resume to prevent timer-throttling "catch-up" storms
    resumeCooldownRef.current = Date.now() + 3000;

    let lastVisibleTime = Date.now();
    
    const onVis = () => {
      const isHidden = document.hidden === true;
      setPagePaused(isHidden);
      
      // If tab is becoming visible after being hidden
      if (!isHidden) {
        const hiddenDuration = Date.now() - lastVisibleTime;
        
        // If tab was hidden for more than 5 minutes, force refresh data to handle stale connections
        if (hiddenDuration > 5 * 60 * 1000) {
          console.log(`🔄 Tab was hidden for ${Math.round(hiddenDuration / 1000 / 60)} minutes - refreshing data...`);
          
          // Force refresh critical data after long inactivity
          if (isAuthenticated) {
            // Prevent request storm on tab focus: let the master poller do a single
            // sequential refresh batch rather than firing multiple requests at once.
            setTimeout(() => {
              window.dispatchEvent(new CustomEvent('masterPoller:fetchPortfolio'));
            }, 800 + Math.floor(Math.random() * 800));
          }
        }
        
        lastVisibleTime = Date.now();
      }
    };
    

    document.addEventListener('visibilitychange', onVis);
    onVis();
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [isAuthenticated]);

  // Update live clock every second
  useEffect(() => {
    const clockInterval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    
    return () => clearInterval(clockInterval);
  }, []);

  // ═══════════════════════════════════════════════════════════════════════════
  // 🚀 MASTER GLOBAL POLLER - Single interval that controls ALL polling
  // This NEVER recreates - guaranteed zero accumulation
  // FIX: Use refs to avoid stale closure over isAuthenticated
  // ═══════════════════════════════════════════════════════════════════════════
  const masterPollerInitialized = useRef(false);
  const isAuthenticatedRef = useRef(isAuthenticated);

  // Global backoff for ALL authenticated polling (prevents 520/502 death spirals)
  const pollingBackoffRef = useRef({ until: 0, factor: 1, lastStatus: null });
  const resumeCooldownRef = useRef(0);
  const shouldSkipPollingRef = () => {
    const now = Date.now();
    const until = pollingBackoffRef.current.until || 0;
    const resumeUntil = resumeCooldownRef.current || 0;
    return now < until || now < resumeUntil;
  };
  const [pollingBackoffUi, setPollingBackoffUi] = useState({ until: 0, factor: 1, lastStatus: null });

  const recordPollingSuccessRef = () => {
    const v = { until: 0, factor: 1, lastStatus: null };
    pollingBackoffRef.current = v;
    setPollingBackoffUi(v);
  };
  const recordPollingFailureRef = (status) => {
    const prev = pollingBackoffRef.current;
    const nextFactor = Math.min((prev.factor || 1) * 2, 4);
    const waitMs = Math.min(30000, 5000 * nextFactor); // cap at 30s, not 2 minutes
    const v = { until: Date.now() + waitMs, factor: nextFactor, lastStatus: status || null };
    pollingBackoffRef.current = v;
    setPollingBackoffUi(v);
  };
  
  // Keep ref in sync with state
  useEffect(() => {
    isAuthenticatedRef.current = isAuthenticated;
  }, [isAuthenticated]);
  
  useEffect(() => {
    // Only initialize once, ever
    if (masterPollerInitialized.current) return;
    masterPollerInitialized.current = true;
    
    // noisy polling log removed
    
    // Timestamps to track when each fetch last happened
    const timestamps = {
      marketData: 0,
      portfolio: 0,
      tradingStatus: 0,
      bootstrap: 0
    };
    
    // Intervals (in milliseconds)
    const INTERVALS = {
      marketData: 120000,    // 120s - market data (disabled anyway)
      portfolio: 10000,      // 10s - portfolio refresh (crypto is 24/7, needs to be responsive)
      tradingStatus: 600000,  // 10m - legacy trading status disabled (kept only for backward UI pieces)
      bootstrap: 60000       // 60s - bootstrap for non-auth
    };
    
    // Master interval - checks every 5 seconds what needs updating
    const masterInterval = setInterval(() => {
      const now = Date.now();
      
      // Global skip: tab is hidden
      if (document.hidden) {
        // noisy polling log removed
        return;
      }
      
      // FIX: Read from ref to get latest auth state
      const isAuth = isAuthenticatedRef.current;
      
      // 1. Bootstrap (only when NOT authenticated)
      if (!isAuth && (now - timestamps.bootstrap >= INTERVALS.bootstrap)) {
        // noisy polling log removed
        // Legacy bootstrap disabled (trading-status removed)
        timestamps.bootstrap = now;
      }
      
      // Only authenticated polling below
      if (!isAuth) return;
      
      // DISABLED: Market data polling - Live Market Data panel removed
      // if (now - timestamps.marketData >= INTERVALS.marketData) {
      //   console.log('📊 Master Poller: Fetching market data');
      //   window.dispatchEvent(new CustomEvent('masterPoller:fetchMarketData'));
      //   timestamps.marketData = now;
      // }
      
      // 3. Portfolio (Hyperliquid state comes from copy-engine/status polling)
      if (now - timestamps.portfolio >= INTERVALS.portfolio) {
        // Global backoff: if backend is returning 5xx, pause polling for a bit
        if (shouldSkipPollingRef()) {
          return;
        }
        console.log('💼 Master Poller: Fetching portfolio & status');
        window.dispatchEvent(new CustomEvent('masterPoller:fetchPortfolio'));
        timestamps.portfolio = now;
      }
      
    }, 5000); // Check every 5 seconds for reduced load
    
    console.log('🔄 Master Poller: Started (5s interval)');
    
    // Cleanup only happens on component unmount (which should be never for main App)
    return () => {
      clearInterval(masterInterval);
      // noisy polling log removed
    };
  }, []); // EMPTY DEPS - never recreates!

  
  // Live 100-symbol set fetched from backend symbol registry after auth
  const [liveSymbols, setLiveSymbols] = useState([]);
  const lastGoodLiveRef = useRef([]);
  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;

    const setAll = (syms) => {
      if (cancelled) return;
      lastGoodLiveRef.current = syms.length > 0 ? syms : (lastGoodLiveRef.current || []);
      const use = syms.length > 0 ? syms : (lastGoodLiveRef.current || []);
      setLiveSymbols(use);
      const init = {};
      use.forEach(s => { init[s] = { loading: true, error: false }; });
      setMarketData(prev => ({ ...init, ...prev }));
      setActiveSymbols(use);
    };

    const sleep = (ms) => new Promise(r => setTimeout(r, ms));

    const fetchActive = async () => {
      try {
        // Crypto grid trading bot — use default crypto symbols directly
        // No legacy /api/symbols/active endpoint needed
        const defaultCryptoSymbols = ['BTC', 'ETH', 'SOL', 'XRP', 'DOGE', 'ADA', 'AVAX', 'DOT', 'LINK', 'LTC'];
        setAll(defaultCryptoSymbols);
      } catch (e) {
        console.warn('Failed to set active symbols:', e?.message || e);
      }
    };

    fetchActive();
    return () => { cancelled = true; };
  }, [isAuthenticated]);

  // REMOVED: Auto-pin solid 100 (legacy stock market feature)
  // Crypto trading bot doesn't need this
  useEffect(() => {
    if (!isAuthenticated) return;
    // Removed pin-solid100 call - not needed for crypto trading
    const t = setTimeout(async () => {
      console.log('✅ Crypto trading mode active - skipping stock market pin');
      // No-op: Crypto bot doesn't need stock symbol pinning
    }, 1500);
    return () => clearTimeout(t);
  }, [isAuthenticated]);

  // Crypto pairs imported from utils/constants.js

  // Categorized universe: Always use crypto pairs (SOLID100)
  const allSymbols = {
    'Live': SOLID100  // Force crypto pairs only
  };
  
  // Get active symbols based on current view mode
  const getActiveSymbols = () => {
    if (viewMode === 'favorites') {
      return favoriteSymbols;
    } else if (viewMode === 'search' && searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const allSymbolsList = Object.values(allSymbols).flat();
      return allSymbolsList.filter(symbol => 
        symbol.toLowerCase().includes(query)
      );
    } else {
      return allSymbols[selectedCategory] || allSymbols.Live;
    }
  };
  
  // Get all symbols as flat array
  const getAllSymbolsFlat = () => {
    return Object.values(allSymbols).flat();
  };

  // Cryptocurrency names imported from utils/constants.js

  // Single useEffect for initial data loading with better tracking
  useEffect(() => {
    // Only fetch data if user is authenticated
    if (isAuthenticated) {
      console.log("🚀 Component mounted - Starting initial data fetch for authenticated user");
      
      // DISABLED: Market data loading - Live Market Data panel removed
      // setTimeout(() => {
      //   loadInitialMarketData();
      // }, 1500);
      
      // DISABLED: Technical analysis panel removed
      // setTimeout(() => { ... }, 500);
      
      // Fetch portfolio/trading data (requires API key)
      fetchData();
      
      // Fetch autonomous/copy engine status with slight delay
      // Moved to single batch to reduce concurrent requests
      setTimeout(() => {
        fetchAutonomousStatus();
        // Trading preferences (notional + leverage) are hydrated by
        // fetchGridBotParams() via GET /api/grid-bot/params — no separate
        // GET /trading-prefs call needed (that endpoint is POST-only).
      }, 1000);
      
      // Portfolio/Status refresh moved to Master Poller
      // Listen for master poller events
      const portfolioBatchInFlightRef = { current: false };

      const handlePortfolioFetch = async () => {
        // Prevent bursts of parallel requests (especially after tab resumes)
        if (portfolioBatchInFlightRef.current) return;
        portfolioBatchInFlightRef.current = true;

        try {
          // Run sequentially to avoid request storms that can trigger 520s on LIVE
          await refreshPortfolioData();
          await new Promise((r) => setTimeout(r, 250));
          await fetchAutonomousStatus();
        } finally {
          portfolioBatchInFlightRef.current = false;
        }
      };
      
      window.addEventListener('masterPoller:fetchPortfolio', handlePortfolioFetch);
      
      return () => {
        window.removeEventListener('masterPoller:fetchPortfolio', handlePortfolioFetch);
      };
    } else {
      // noisy polling log removed
      setLoading(false); // Set loading to false for unauthenticated users
    }
  }, [isAuthenticated]); // Depend on authentication status

  // Low-Impact Mode: sequential refresh with visibility + closed-market pause + visible-only
  // NOTE: Disabled when bulk mode is active
  const USE_BULK_LATEST = true;
  // Dynamic bulk batch size: start with 20, ramp to 100 after 30s post-login
  const [bulkBatchSize, setBulkBatchSize] = useState(100);

  // Use refs for sequential polling to avoid recreating intervals
  const favSymbolsSeqRef = useRef(favoriteSymbols);
  const visSymbolsSeqRef = useRef(visibleSymbols);
  
  useEffect(() => {
    favSymbolsSeqRef.current = favoriteSymbols;
    visSymbolsSeqRef.current = visibleSymbols;
  }, [favoriteSymbols, visibleSymbols]);

  // DISABLED: Sequential market data polling - Live Market Data panel removed
  // useEffect(() => {
  //   if (!isAuthenticated || USE_BULK_LATEST) return;
  //   ... sequential polling disabled ...
  // }, [isAuthenticated, pagePaused]);

  // Bulk latest per page to remove >100 symbol bottleneck
  const PAGE_BATCH = bulkBatchSize; // dynamic, default 100
  const BULK_INTERVAL_MS = 30000; // 30 seconds - CRITICAL: prevent backend overload
  const BULK_JITTER_MS = 5000; // ±5 second jitter to spread out requests

  // prevent overlapping bulk requests and honor in-flight state
  const bulkInFlightRef = useRef(false);
  const lastBulkAtRef = useRef(0);
  const abortControllerRef = useRef(null);
  const consecutiveErrorsRef = useRef(0);
  
  // Use refs for dynamic values to avoid recreating intervals on every change
  const favSymbolsRef = useRef(favoriteSymbols);
  const visSymbolsRef = useRef(visibleSymbols);
  const liveSymbolsRef = useRef(liveSymbols);
  
  useEffect(() => {
    favSymbolsRef.current = favoriteSymbols;
    visSymbolsRef.current = visibleSymbols;
    liveSymbolsRef.current = liveSymbols;
  }, [favoriteSymbols, visibleSymbols, liveSymbols]);

  const fetchMarketDataBulk = async (symbols) => {
    try {
      if (!Array.isArray(symbols) || symbols.length === 0) return;
      const now = Date.now();
      if (bulkBackoffRef.current.until && now < bulkBackoffRef.current.until) return; // in backoff window
      if (bulkInFlightRef.current) {
        console.log('⏸️ Skipping market data - request already in flight');
        return; // skip overlapping
      }
      
      // CIRCUIT BREAKER: Exponential backoff on consecutive errors
      const minInterval = 10000 * Math.pow(2, Math.min(consecutiveErrorsRef.current, 3)); // 10s, 20s, 40s, 80s max
      if (now - lastBulkAtRef.current < minInterval) {
        console.log(`⏸️ Skipping market data - rate limited (${minInterval/1000}s min)`);
        return;
      }
      
      bulkInFlightRef.current = true;

      // Cancel any previous request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      const trimmed = symbols.filter(Boolean).map(s => String(s).toUpperCase());
      // Use the GET market-data endpoint (no auth required)
      const resp = await axios.get(`${API}/market-data`, { 
        timeout: 30000,  // Increased timeout for slow Coinbase fallback
        signal: abortControllerRef.current.signal
      });
      const data = resp.data || {};
      const results = data.market_data || {}; // Backend returns market_data key
      setMarketData(prev => {
        const next = { ...prev };
        Object.keys(results).forEach((sym) => {
          const r = results[sym] || {};
          const priceNum = parseFloat(r.price || 0);
          let clean = {
            ...r,
            price: isFinite(priceNum) ? priceNum : 0,
            change: parseFloat(r.change_24h || 0),
            change_percent: parseFloat(r.change_24h || 0),
            loading: false,
            error: r.error ? true : false,
            status: r.error ? 'error' : 'success'
          };
          if (clean.price > 10000000 || clean.price < 0) clean.price = 0;
          next[sym] = clean;
        });
        return next;
      });
      // success: reset backoff and mark timestamps
      bulkBackoffRef.current = { until: 0, factor: 1 };
      lastBulkAtRef.current = Date.now();
      consecutiveErrorsRef.current = 0; // Reset error counter on success
      console.log('✅ Market data fetched successfully');
    } catch (e) {
      // Don't log abort errors (they're intentional)
      if (e.name !== 'CanceledError' && e.name !== 'AbortError') {
        console.warn('Bulk latest fetch failed:', e?.message || e);
        consecutiveErrorsRef.current += 1;
        console.log(`⚠️ Consecutive errors: ${consecutiveErrorsRef.current}`);
      }
      
      setMarketData(prev => {
        const next = { ...prev };
        (symbols || []).forEach(sym => {
          next[sym] = { ...(next[sym]||{}), loading: false, error: true, status: 'error' };
        });
        return next;
      });
      // backoff: exponential up to 60s
      const f = Math.min((bulkBackoffRef.current.factor || 1) * 2, 32);
      const waitMs = Math.min(60000, 2000 * f);
      bulkBackoffRef.current = { until: Date.now() + waitMs, factor: f };
    } finally {
      bulkInFlightRef.current = false;
      abortControllerRef.current = null;
    }
  };

  // DISABLED: Market data fetching - Live Market Data panel removed
  // useEffect(() => {
  //   if (!isAuthenticated || !USE_BULK_LATEST) return;
  //   ... market data polling code disabled ...
  // }, [isAuthenticated, pagePaused]);

  // Fetch AI decisions periodically
  // AI decisions polling removed - using Moon-Spread instead

  // Fetch AI thoughts/reasoning when panel is opened
  const fetchAiThoughts = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const response = await axios.get(`${BACKEND_URL}/api/ai-thoughts`, { 
        headers,
        timeout: 10000 
      });
      setAiThoughts(response.data);
      console.log("✅ AI thoughts loaded:", response.data.score);
    } catch (error) {
      console.warn("Failed to fetch AI thoughts:", error);
    }
  };

  // Legacy market/trading status subsystem disabled for this build.
  const refreshMarketStatus = async () => {
    return;
  };

  // Fetch trading stats
  const fetchTradingStats = async () => {
    // Prevent duplicate in-flight requests
    if (inFlightRef.current.tradingStats) {
      console.log('⏸️ Skipping fetchTradingStats - already in flight');
      return;
    }
    inFlightRef.current.tradingStats = true;
    
    try {
      const headers = {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      };

      // Use MONTHLY stats endpoint instead of today
      const response = await axios.get(`${BACKEND_URL}/api/user/trading-stats/monthly`, {
        headers,
        timeout: 15000
      });
      
      if (response.status === 200) {
        const stats = response.data;
        
        // Show MONTHLY stats in the main dashboard - map all fields from backend
        setTradingStats({
          month: stats.month,
          trades_executed: stats.trades_executed || 0,
          trades_opened: stats.trades_opened || 0,
          trades_closed: stats.trades_closed || 0,
          trades_profitable: stats.trades_profitable || 0,
          success_rate: stats.success_rate || 0,
          realized_pnl: stats.realized_pnl || 0,
          unrealized_pnl: stats.unrealized_pnl || 0,
          total_pnl: stats.total_pnl || 0,
          current_positions: stats.current_positions || 0,
          last_trade: stats.last_trade,
          status: stats.status,
          days_until_reset: stats.days_until_reset,
          currency: stats.currency || 'USD',
        });
        console.log("✅ Monthly trading stats loaded:", stats);
      }
    } catch (error) {
      console.warn("Failed to fetch trading stats:", error);
      setTradingStats({
        trades_executed: 0,
        trades_opened: 0,
        trades_closed: 0,
        realized_pnl: 0,
        unrealized_pnl: 0,
        total_pnl: 0,
        current_positions: 0,
        last_trade: null,
        status: "error"
      });
    } finally {
      inFlightRef.current.tradingStats = false;
    }
  };
  
  // Hyperliquid Copy Trading functions
  // Grid Bot dashboard is the single source of truth for running state.
  // This callback is called by GridBotDashboard when state changes.
  //
  // OBSERVABILITY: log every transition of `user_participating` so when the
  // bot stops by itself you can see WHEN and WHY in the browser console
  // (DevTools → Console) without needing backend logs. The previous_running
  // ref captures the prior value so we can detect transitions, not just
  // re-render with the same value.
  const previousRunningRef = useRef(null);
  const lastTransitionLogRef = useRef(0);
  // Diff-trackers so we only log state CHANGES (no spam on every poll).
  const lastIdleReasonRef = useRef('');
  const lastRegimeRef = useRef('');
  const lastGuardActionRef = useRef('none');
  const lastStopReasonKeyRef = useRef('');
  // Surface the actual reason the bot stopped (server-truth) so the user
  // sees more than just "Stopped" if it crashed mid-trade. Stored as
  // null when no informative reason is available (e.g., manual stop, or
  // never stopped). 22 May 2026.
  const [lastStopReason, setLastStopReason] = useState(null);
  // Live bot regime + coin from the most recent dashboard payload.
  // Used by the coin selector to override the stateless `/regime-preview`
  // pill for the currently-traded coin — otherwise users see e.g. the
  // pill saying RANGING while the bot's Hydra shows MILD TREND (the
  // bot uses hysteresis; the preview is stateless). 23 May 2026.
  const [liveBotRegime, setLiveBotRegime] = useState(null);
  const [liveBotCoin, setLiveBotCoin] = useState(null);

  const handleGridBotStatusChange = useCallback((isRunning, state, payload = {}) => {
    const smartStop = payload?.smart_stop || {};
    const buyResumeIn = Number(smartStop.buy_resume_in || 0);
    const sellResumeIn = Number(smartStop.sell_resume_in || 0);
    const cooldownSecondsRemaining = Math.max(0, Math.max(buyResumeIn, sellResumeIn));
    const cooldownActive = cooldownSecondsRemaining > 0;
    const rawRegime = payload?.hydra?.regime;
    const hydraRegime = typeof rawRegime === 'string'
      ? rawRegime
      : (rawRegime?.regime || null);
    const nonRangingEntryPauseActive = Boolean(payload?.non_ranging_entry_pause_active);
    const regimeFilterPauseActive = Boolean(payload?.regime_filter_pause_active);
    const regimeFilterDisallowedRegime = payload?.regime_filter_disallowed_regime || null;

    // Sync allowed_trade_regimes from dashboard payload
    if (Array.isArray(payload?.allowed_trade_regimes)) {
      setAllowedTradeRegimes(payload.allowed_trade_regimes);
    }
    // Sync live bot regime + active coin so the coin-selector pill
    // shows what the engine SEES (with hysteresis) for the currently-
    // traded coin, instead of the stateless preview.
    if (isRunning) {
      setLiveBotRegime(hydraRegime || null);
      setLiveBotCoin(payload?.coin || payload?.params?.symbol || null);
    } else {
      setLiveBotRegime(null);
      setLiveBotCoin(null);
    }

    // ── Diff-based state-change console logs (Feb 2026) ─────
    // Log only when state actually CHANGES — keeps console clean while
    // surfacing every meaningful transition (regime flip, guard trip,
    // idle-reason change). Each call is a console.log via window.__botLog
    // tagged so DevTools filter chips work.
    const idleReason = payload?.hydra?.idle_reason || '';
    if (idleReason !== lastIdleReasonRef.current) {
      window.__botLog?.('IDLE_REASON',
        `"${lastIdleReasonRef.current}" → "${idleReason}"`,
        { running: isRunning, regime: hydraRegime, coin: payload?.coin });
      lastIdleReasonRef.current = idleReason;
    }
    if (hydraRegime && hydraRegime !== lastRegimeRef.current) {
      window.__botLog?.('REGIME',
        `"${lastRegimeRef.current}" → "${hydraRegime}"`,
        { adx: payload?.hydra?.regime?.adx,
          coin: payload?.coin,
          spacing: payload?.hydra?.volatility?.current_spacing_pct });
      lastRegimeRef.current = hydraRegime;
    }
    const guardAction = payload?.hydra?.risk_guards?.action || 'none';
    if (guardAction !== lastGuardActionRef.current) {
      window.__botLog?.('RISK_GUARD',
        `"${lastGuardActionRef.current}" → "${guardAction}"`,
        { driver: payload?.hydra?.risk_guards?.driver,
          tripped: payload?.hydra?.risk_guards?.tripped });
      lastGuardActionRef.current = guardAction;
    }

    // Diff-log last_stop_reason whenever it changes (Feb 2026). Backend
    // includes it on every NOT-RUNNING dashboard payload so the user sees
    // WHY the bot died even if they opened the page after the fact.
    const stopReason = payload?.last_stop_reason;
    if (stopReason && stopReason.reason) {
      const key = `${stopReason.reason}@${stopReason.at}`;
      if (key !== lastStopReasonKeyRef.current) {
        const color = stopReason.reason === 'manual' ? 'color:#9ca3af'
          : stopReason.reason === 'crashed' ? 'color:#ef4444'
          : stopReason.reason === 'cancelled' ? 'color:#f59e0b'
          : stopReason.reason === 'rate_limited' ? 'color:#fb923c'
          : 'color:#facc15';
        // eslint-disable-next-line no-console
        console.warn(
          `%c[BOT STOPPED] ${stopReason.reason.toUpperCase()}`,
          color,
          { detail: stopReason.detail,
            at: new Date(stopReason.at * 1000).toISOString() }
        );
        lastStopReasonKeyRef.current = key;
      }
    }

    // Detect & log lifecycle transitions for live debugging.
    const prevRunning = previousRunningRef.current;
    if (prevRunning !== null && prevRunning !== isRunning) {
      const now = Date.now();
      // Throttle (≥500ms between transition logs to avoid flapping noise)
      if (now - lastTransitionLogRef.current > 500) {
        lastTransitionLogRef.current = now;
        const ts = new Date().toISOString();
        const ctx = {
          ts,
          from: prevRunning ? 'RUNNING' : 'STOPPED',
          to: isRunning ? 'RUNNING' : 'STOPPED',
          engine_state: state,
          trading_status: payload?.trading_status,
          hydra_regime: hydraRegime,
          regime_filter_pause_active: regimeFilterPauseActive,
          regime_filter_disallowed_regime: regimeFilterDisallowedRegime,
          cooldown_active: cooldownActive,
          cooldown_seconds_remaining: cooldownSecondsRemaining,
          cb_flatten_active: Boolean(payload?.cb_flatten_active),
          smart_stop: smartStop,
          source: payload?._source || 'gridbot-dashboard-poll',
        };
        if (!isRunning) {
          // Loud warn so it's easy to find in console.
          console.warn('[BOT LIFECYCLE] ⚠️ STOPPED', ctx);
          // Ask backend for the last-stop reason for this user, then
          // surface it on the dashboard if it was unexpected (crashed
          // or cancelled). Manual stops are silent — user knows.
          (async () => {
            try {
              const resp = await axios.get(`${BACKEND_URL}/api/grid-bot/last-stop-reason`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
                timeout: 10000,
              });
              const data = resp.data || {};
              console.warn('[BOT LIFECYCLE] last-stop-reason from backend:', data);
              if (data.has_reason && data.reason && data.reason !== 'manual') {
                setLastStopReason({
                  reason: data.reason,
                  detail: data.detail || '',
                  at: data.at || 0,
                });
              }
            } catch (e) {
              console.warn('[BOT LIFECYCLE] could not fetch last-stop-reason:', e.message);
            }
          })();
        } else {
          console.log('[BOT LIFECYCLE] ✅ STARTED', ctx);
          // Bot is running again — clear any prior stop banner.
          setLastStopReason(null);
        }
      }
    }
    previousRunningRef.current = isRunning;

    setAutonomousStatus(prev => ({
      ...prev,
      autonomous_mode: isRunning,
      is_running: isRunning,
      engine_running: isRunning,
      user_plugged: isRunning,
      user_participating: isRunning,
      engine_state: state || 'stopped',
      trading_status: payload?.trading_status || (isRunning ? 'active' : 'stopped'),
      cooldown_active: cooldownActive,
      cooldown_seconds_remaining: cooldownSecondsRemaining,
      hydra_regime: hydraRegime,
      non_ranging_entry_pause_active: nonRangingEntryPauseActive,
      regime_filter_pause_active: regimeFilterPauseActive,
      regime_filter_disallowed_regime: regimeFilterDisallowedRegime,
      cb_flatten_active: Boolean(payload?.cb_flatten_active),
      mode: 'LIVE',
    }));
  }, []);

  useEffect(() => {
    setCooldownRemainingSec(autonomousStatus.cooldown_seconds_remaining || 0);
  }, [autonomousStatus.cooldown_seconds_remaining]);

  useEffect(() => {
    if (cooldownRemainingSec <= 0) return;
    const timerId = setInterval(() => {
      setCooldownRemainingSec(prev => Math.max(0, prev - 1));
    }, 1000);
    return () => clearInterval(timerId);
  }, [cooldownRemainingSec]);

  // fetchAutonomousStatus is now a no-op — state comes from GridBotDashboard callback
  const fetchAutonomousStatus = useCallback(async () => {}, []);

  const fetchGridBotParams = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/grid-bot/params`, {
        timeout: 10000,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.status === 200) {
        setAllowedTradeRegimes(Array.isArray(response.data?.allowed_trade_regimes) ? response.data.allowed_trade_regimes : ['ranging']);
        // Hydrate notional/leverage from persisted user prefs. When the DB
        // has no value yet (new user or unset field), explicitly reset to
        // defaults $1 / 10x so the UI reflects what the bot will actually
        // use on the next start.
        const dbNotional = response.data?.trading_notional;
        setTradingNotional(dbNotional != null ? Number(dbNotional) : 1);
        const dbLeverage = response.data?.trading_leverage;
        setTradingLeverage(dbLeverage != null ? Number(dbLeverage) : 10);
        const dbCoin = response.data?.trading_coin;
        if (dbCoin) setTradingCoin(String(dbCoin).toUpperCase());
        setAutoRotateCoin(Boolean(response.data?.auto_rotate_coin));
        setCutAndTrail(Boolean(response.data?.cut_and_trail_enabled));
        setCtAdaptive(Boolean(response.data?.cut_and_trail_adaptive_pctl));
        setHybridExit(Boolean(response.data?.hybrid_exit_enabled));
        if (Array.isArray(response.data?.supported_coins) && response.data.supported_coins.length > 0) {
          setSupportedCoins(response.data.supported_coins);
        }
        if (response.data?.max_leverage_by_coin && typeof response.data.max_leverage_by_coin === 'object') {
          setMaxLeverageByCoin(response.data.max_leverage_by_coin);
        }
      }
    } catch (error) {
      console.warn('Failed to fetch grid bot params:', error);
    }
  }, []);

  const toggleAllowedTradeRegime = async (regime) => {
    try {
      setAllowedTradeRegimesLoading(true);
      const currentlyAllowed = allowedTradeRegimes.includes(regime);
      const nextRegimes = currentlyAllowed
        ? allowedTradeRegimes.filter((value) => value !== regime)
        : [...allowedTradeRegimes, regime];
      const response = await axios.post(
        `${BACKEND_URL}/api/grid-bot/params`,
        { allowed_trade_regimes: nextRegimes },
        {
          timeout: 15000,
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.status === 200) {
        const appliedRegimes = response.data?.applied?.allowed_trade_regimes || nextRegimes;
        setAllowedTradeRegimes(appliedRegimes);
        const regimeLabel = regime === 'mild_trend' ? 'mild trend' : regime.replace('_', ' ');
        const isNowAllowed = appliedRegimes.includes(regime);
        setSuccessMessage(isNowAllowed ? `${regimeLabel} resumed.` : `${regimeLabel} paused.`);
        setError(null);
      }
    } catch (error) {
      setError(error.response?.data?.detail || error.response?.data?.error || 'Failed to update allowed trade regimes');
    } finally {
      setAllowedTradeRegimesLoading(false);
    }
  };

  const toggleAutoRotateCoin = async () => {
    const next = !autoRotateCoin;
    setAutoRotateLoading(true);
    setAutoRotateCoin(next); // optimistic
    try {
      await axios.post(
        `${BACKEND_URL}/api/grid-bot/trading-prefs`,
        { auto_rotate_coin: next },
        {
          timeout: 15000,
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      setSuccessMessage(next
        ? 'Auto-rotate enabled — bot will hop to the best ranging coin when the current one trends.'
        : 'Auto-rotate disabled.');
      setError(null);
    } catch (error) {
      setAutoRotateCoin(!next); // revert on failure
      setError(error.response?.data?.detail || 'Failed to update auto-rotate setting');
    } finally {
      setAutoRotateLoading(false);
    }
  };

  const toggleCutAndTrail = async () => {
    const next = !cutAndTrail;
    setCutAndTrailLoading(true);
    setCutAndTrail(next); // optimistic
    try {
      await axios.post(
        `${BACKEND_URL}/api/grid-bot/trading-prefs`,
        { cut_and_trail_enabled: next },
        {
          timeout: 15000,
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      setSuccessMessage(next
        ? 'Cut & Trail enabled — bot will dump losers fast and trail winners. Takes effect on next Start.'
        : 'Cut & Trail disabled — back to the standard take-profit exits.');
      setError(null);
    } catch (error) {
      setCutAndTrail(!next); // revert on failure
      setError(error.response?.data?.detail || 'Failed to update Cut & Trail setting');
    } finally {
      setCutAndTrailLoading(false);
    }
  };

  const toggleCtAdaptive = async () => {
    const next = !ctAdaptive;
    setCtAdaptiveLoading(true);
    setCtAdaptive(next); // optimistic
    try {
      const resp = await axios.post(
        `${BACKEND_URL}/api/grid-bot/trading-prefs`,
        { cut_and_trail_adaptive_pctl: next, ...(next ? { cut_and_trail_enabled: true } : {}) },
        {
          timeout: 15000,
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      if (next) {
        setCutAndTrail(true); // adaptive requires CT mode on
        const eq = resp?.data?.ct_trial?.start_equity;
        setSuccessMessage(
          `Design-C adaptive trial ENABLED${eq ? ` — trial-start equity $${Number(eq).toFixed(2)} recorded, 8% kill-guard armed` : ' — 8% kill-guard armed on first tick'}. Press Start on ADA to begin.`
        );
      } else {
        setSuccessMessage('Design-C adaptive trial disabled — back to fixed Cut & Trail geometry.');
      }
      setError(null);
    } catch (error) {
      setCtAdaptive(!next); // revert on failure
      setError(error.response?.data?.detail || 'Failed to update adaptive-trial setting');
    } finally {
      setCtAdaptiveLoading(false);
    }
  };

  const toggleHybridExit = async () => {
    const next = !hybridExit;
    setHybridExitLoading(true);
    setHybridExit(next); // optimistic
    try {
      await axios.post(
        `${BACKEND_URL}/api/grid-bot/trading-prefs`,
        { hybrid_exit_enabled: next },
        {
          timeout: 15000,
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      setSuccessMessage(next
        ? 'Hybrid Exit enabled — trend-aligned trades let profit run (never close red); range trades keep the fast TP. Takes effect on next Start.'
        : 'Hybrid Exit disabled — back to the standard take-profit exits.');
      setError(null);
    } catch (error) {
      setHybridExit(!next); // revert on failure
      setError(error.response?.data?.detail || 'Failed to update Hybrid Exit setting');
    } finally {
      setHybridExitLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated && localStorage.getItem('token')) {
      fetchGridBotParams();
    } else if (!isAuthenticated) {
      setAllowedTradeRegimes(['ranging', 'mild_trend', 'strong_trend']);
    }
  }, [fetchGridBotParams, isAuthenticated]);

  const startAutonomousTrading = async () => {
    try {
      setAutonomousLoading(true);
      
      // FIX 22: Increase timeout — start endpoint now waits for init (up to 15s)
      const response = await axios.post(`${BACKEND_URL}/api/grid-bot/start`, {
        preset: "conservative",
        notional_per_level: tradingNotional,
        leverage: tradingLeverage,
      }, {
        timeout: 25000,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.status === 200) {
        const result = response.data;
        
        if (result.status === 'started' || result.status === 'already_running' || result.status === 'starting') {
          setSuccessMessage(result.message || 'Grid Bot trading started!');
          
          setAutonomousStatus(prev => ({
            ...prev,
            is_running: true,
            user_participating: true,
          }));
          
          setTimeout(async () => {
            await fetchAutonomousStatus();
          }, 1000);
        } else {
          const errorMsg = result.detail || result.message || 'Failed to start Grid Bot';
          setActionError(errorMsg);
        }
      }
    } catch (error) {
      const errData = error.response?.data;
      let errorMsg = errData?.detail || errData?.error || error.message;
      // Don't double-prefix when backend already says "Failed to start bot: ..."
      const prefix = /^failed to start/i.test(String(errorMsg)) ? '' : 'Failed to start: ';
      setActionError(prefix + errorMsg);
      // FIX 22: If start failed, ensure UI shows stopped
      setAutonomousStatus(prev => ({
        ...prev,
        is_running: false,
        user_participating: false,
      }));
    } finally {
      setAutonomousLoading(false);
    }
  };

  const stopAutonomousTrading = async (closePositions = false) => {
    if (autonomousLoading) return;
    
    try {
      setAutonomousLoading(true);
      
      setAutonomousStatus(prev => ({
        ...prev,
        user_participating: false
      }));

      // Stop the grid bot
      const resp = await axios.post(`${BACKEND_URL}/api/grid-bot/stop`, {}, {
        timeout: 15000,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (resp.status === 200 && resp.data?.status === 'stop_pending') {
        // Engine hasn't confirmed the stop yet (e.g. parked in a rate-limit).
        // Honest, non-blocking notice + re-poll — do NOT claim it stopped.
        setActionError(resp.data?.message || 'Stop requested — waiting for the engine to confirm. Refresh in a minute.');
        setTimeout(() => {
          fetchAutonomousStatus().catch(console.warn);
          refreshPortfolioData(true);
        }, 3000);
        return;
      }

      if (resp.status === 200 && (resp.data?.status === 'stopped' || resp.data?.status === 'not_running')) {
        // If user also wants to close all positions (Stop Trading button always
        // passes true so the bot stops AND positions/orders are flattened).
        if (closePositions) {
          try {
            const closeResp = await axios.post(`${BACKEND_URL}/api/hyperliquid/close-all`, {}, {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                'Content-Type': 'application/json'
              },
              timeout: 30000
            });
            const closed = closeResp.data?.closed || [];
            const ordersCancelled = closeResp.data?.orders_cancelled || 0;
            const parts = ['Bot stopped'];
            if (ordersCancelled > 0) parts.push(`${ordersCancelled} order(s) cancelled`);
            if (closed.length > 0) parts.push(`${closed.length} position(s) closed`);
            setSuccessMessage(`✅ ${parts.join(' · ')}`);
          } catch (closeErr) {
            // Bot is already stopped; just inform the user to retry close-all.
            // Do NOT use setError — that nukes the whole dashboard.
            const status = closeErr.response?.status;
            const reason = status === 429
              ? 'Hyperliquid rate-limited the request.'
              : (closeErr.response?.data?.error || closeErr.message || 'unknown error');
            setActionError(`Bot stopped, but flatten failed: ${reason} Tap "Close All Positions" to retry.`);
          }
        } else {
          setSuccessMessage('Grid Bot stopped.');
        }
        
        setAutonomousStatus(prev => ({
          ...prev,
          is_running: false,
          user_participating: false,
        }));
        
        setTimeout(() => {
          fetchAutonomousStatus().catch(console.warn);
          refreshPortfolioData(true);
        }, 1000);
      } else {
        throw new Error(resp.data?.message || 'Stop failed');
      }
    } catch (error) {
      setActionError('Failed to stop: ' + (error.response?.data?.detail || error.response?.data?.error || error.message));
    } finally {
      setAutonomousLoading(false);
    }
  };

  // Sync Individual Position
  const [syncingCoin, setSyncingCoin] = useState(null);

  const syncSinglePosition = async (coin, traderAddress = null) => {
    // Copy engine removed — sync not available
    setSyncError('Position sync is not available. Use Grid Bot for trading.');
    setTimeout(() => setSyncError(null), 5000);
  };

  // Close all positions on Hyperliquid
  const [closingPositions, setClosingPositions] = useState(false);
  const [closingCoin, setClosingCoin] = useState(null); // Track which coin is being closed
  const [syncingPositions, setSyncedPositions] = useState(false);
  const [showSyncConfirm, setShowSyncConfirm] = useState(false);
  const [syncError, setSyncError] = useState(null); // Show sync errors as toast, not full page crash
  
  // Close individual position
  const closePosition = async (coin) => {
    if (!coin) return;
    
    try {
      setClosingCoin(coin);
      // NOTE: do NOT touch global `error` state here. That state drives
      // the full-screen "Error Loading Dashboard" view, and a transient
      // close failure should not nuke the whole dashboard. Surface the
      // error as a non-blocking message instead.
      setSuccessMessage(null);
      
      const response = await axios.post(`${BACKEND_URL}/api/hyperliquid/close-position/${coin}`, {}, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        timeout: 30000
      });
      
      if (response.data.status === 'success') {
        setSuccessMessage(`✅ Closed ${coin} position @ $${response.data.avg_price?.toFixed(2) || 'market'}`);
        // Refresh portfolio
        setTimeout(() => {
          refreshPortfolioData(true);
        }, 1500);
      } else {
        setSuccessMessage(`❌ ${response.data.error || `Failed to close ${coin} position`}`);
      }
    } catch (error) {
      console.error(`Failed to close ${coin} position:`, error);
      setSuccessMessage(`❌ ${error.response?.data?.error || error.message || `Failed to close ${coin}`}`);
    } finally {
      setClosingCoin(null);
    }
  };
  
  const closeAllPositions = async () => {
    // Show modern React modal instead of ugly window.confirm
    setShowCloseAllConfirm(true);
  };

  const executeCloseAllPositions = async () => {
    setShowCloseAllConfirm(false);
    
    try {
      setClosingPositions(true);
      // NOTE: do NOT touch global `error` state for close-all failures.
      // That state drives the full-screen "Error Loading Dashboard" view,
      // and a transient 429 / partial failure should not nuke the entire
      // dashboard. Surface as a non-blocking toast (actionError) instead.
      setActionError(null);
      
      const response = await axios.post(`${BACKEND_URL}/api/hyperliquid/close-all`, {}, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        timeout: 30000
      });
      
      if (response.data.status === 'success' || response.data.status === 'partial') {
        const closed = response.data.closed || [];
        const ordersCancelled = response.data.orders_cancelled || 0;
        const parts = [];
        if (closed.length > 0) {
          parts.push(`Closed ${closed.length} position(s): ${closed.map(p => p.coin).join(', ')}`);
        }
        if (ordersCancelled > 0) {
          parts.push(`Cancelled ${ordersCancelled} open order(s)`);
        }
        if (parts.length > 0) {
          setSuccessMessage(`✅ ${parts.join('. ')}`);
        } else {
          setSuccessMessage('✅ No open positions or orders to close');
        }
        
        // Refresh portfolio after a short delay for cache to clear
        setTimeout(() => {
          fetchAutonomousStatus();
          refreshPortfolioData(true);
        }, 2000);
      } else if (response.data.failed && response.data.failed.length > 0) {
        setActionError(`Some positions failed to close: ${response.data.failed.map(f => f.coin + ': ' + f.error).join(', ')}. Tap Close All again to retry.`);
      } else {
        const raw = response.data.error || 'Failed to close positions';
        const looksRateLimited = /429|rate.?limit/i.test(raw);
        setActionError(
          looksRateLimited
            ? 'Hyperliquid rate-limited the request. Wait a few seconds and tap Close All again.'
            : `${raw}. Tap Close All again to retry.`
        );
      }
    } catch (error) {
      console.error('Failed to close positions:', error);
      if (error.response?.status === 401) {
        // Genuine session-expired case: full-screen prompt is OK because the
        // user must re-auth anyway.
        setError('Session expired. Please log in again.');
      } else if (error.response?.status === 429) {
        setActionError('Hyperliquid rate-limited the request. Wait a few seconds and tap Close All again.');
      } else {
        setActionError('Close All failed: ' + (error.response?.data?.error || error.response?.data?.detail || error.message) + '. Tap Close All again to retry.');
      }
    } finally {
      setClosingPositions(false);
    }
  };

  const emergencyStopTrading = async (reason = "User triggered emergency stop") => {
    try {
      setAutonomousLoading(true);
      const headers = {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      };

      const response = await axios.post(`${BACKEND_URL}/api/grid-bot/stop`, {}, {
        headers,
        timeout: 15000
      });
      
      if (response.status === 200) {
        const result = response.data;
        if (result.status === 'success') {
          setSuccessMessage('Emergency stop activated');
          await fetchAutonomousStatus(); // Refresh status
        } else {
          setError(result.error || 'Failed to activate emergency stop');
        }
      }
    } catch (error) {
      console.error('Failed to activate emergency stop:', error);
      setError('Failed to activate emergency stop: ' + (error.response?.data?.detail || error.message));
    } finally {
      setAutonomousLoading(false);
    }
  };

  // Sync Button - Shows modern modal confirmation
  const handleSyncPositions = () => {
    setShowSyncConfirm(true);
  };

  const executeSyncPositions = async () => {
    // Copy engine removed — sync not available
    setSyncError('Position sync is not available. Use Grid Bot for trading.');
    setTimeout(() => setSyncError(null), 5000);
    setShowSyncConfirm(false);
  };

  // NEW: Separate function to refresh portfolio data
  const lastPortfolioRefreshRef = useRef(0);
  
  const refreshPortfolioData = async (forceRefresh = false) => {
    // Throttle: minimum 2 seconds between refreshes
    // NOTE: even forced refresh must respect throttle to avoid Hyperliquid 429s.
    const now = Date.now();
    if ((now - lastPortfolioRefreshRef.current) < 2000) {
      return;
    }
    lastPortfolioRefreshRef.current = now;
    

    // Global backoff: if backend is failing, do not spam
    if (!forceRefresh && shouldSkipPollingRef()) {
      return;
    }

    try {
      console.log("🔄 Refreshing portfolio data...", forceRefresh ? "(forced)" : "");
      const url = forceRefresh 
        ? `${BACKEND_URL}/api/user/portfolio?refresh=true&t=${Date.now()}`
        : `${BACKEND_URL}/api/user/portfolio`;
      const portfolioResponse = await axios.get(url, {
        timeout: 30000 // 30 second timeout
      });
      
      const portfolioData = portfolioResponse.data;
      recordPollingSuccessRef();
      // noisy polling log removed
      
      if (!portfolioData.error && portfolioData.cash !== undefined && portfolioData.total_value !== undefined) {
        // noisy polling log removed
        setPortfolio({
          cash: portfolioData.cash,
          total_value: portfolioData.total_value,
          available_margin: portfolioData.available_margin || 0,
          positions: portfolioData.positions || [],
          daily_pnl: portfolioData.daily_pnl || 0,
          total_pnl: portfolioData.total_pnl || 0,
          currency: portfolioData.currency || 'USD',
          paper_trading: portfolioData.paper_trading || false,
          loading: false,
          lastUpdated: Date.now(),
          isStale: portfolioData.stale || false,  // Backend can return stale cached data
          note: portfolioData.note // Capture note from backend
        });
        // noisy polling log removed
      } else if (portfolioData.error) {
        // If backend surfaced a rate-limit error, enter polling backoff.
        const errStr = String(portfolioData.error || '');
        if (errStr.includes('rate limited') || errStr.includes('429')) {
          recordPollingFailureRef(429);
          setPortfolio(prev => ({
            ...prev,
            loading: false,
            isStale: true,
            error: 'Rate limited (will retry automatically)'
          }));
          return;
        }

        console.error("❌ Portfolio error from backend:", portfolioData.error);
        setPortfolio(prev => ({
          ...prev,
          loading: false,
          error: portfolioData.error // Capture error to display in UI
        }));
      }
    } catch (error) {
      const status = error?.response?.status;

      // Backoff on 429s and 5xx to avoid hammering Hyperliquid via backend.
      if (status === 429 || status >= 500 || status === 520) {
        recordPollingFailureRef(status);
      }

      // Avoid dumping the full backend tuple into the UI. Keep details in console.
      console.warn("⚠️ Portfolio refresh failed:", error);

      // Mark data as stale but preserve last known values
      setPortfolio(prev => ({
        ...prev,
        loading: false,
        isStale: true,
        error: status === 429 ? 'Rate limited (will retry automatically)' : prev.error
      }));
    }
  };



  const fetchData = async (retryCount = 0, subscriptionErrorHandled = false) => {
    const maxRetries = 0; // Disable multi-retry loops to avoid long blocking during login
    
    // Only set loading true on first attempt
    if (retryCount === 0) {
      setLoading(true);
      setError(null); // Clear any previous errors
    }
    
    try {
      console.log(`🚀 Fetching portfolio data (attempt ${retryCount + 1}/${maxRetries + 1})`);
      
      // Legacy trading-status subsystem disabled in this build.
      // Hyperliquid copy-trading status comes from /api/hyperliquid/copy-engine/status.
      // Keep existing tradingStatus - don't reset to null
      
      // Set loading state but preserve existing portfolio values
      setPortfolio(prev => ({
        ...prev,
        loading: true  // Keep loading true while we fetch portfolio
      }));
      
      // Allow UI to render with complete trading status (including time)
      setLoading(false);
      
      console.log("📡 Now fetching portfolio data in background...");
      
      // Fetch portfolio data with tighter timeout (do not block login forever)
      let portfolioSuccess = false;
      try {
        const portfolioResponse = await axios.get(`${BACKEND_URL}/api/user/portfolio`, { 
          timeout: 12000 // 12s max for portfolio
        });
        
        console.log("📊 Portfolio API response received successfully");
        const portfolioData = portfolioResponse.data;
        // noisy polling log removed
        
        // Validate that we received actual data, not error response
        // If backend is rate-limited but returns cached data as stale, do not throw.
        if (portfolioData.error) {
          const errStr = String(portfolioData.error || '');
          if (errStr.includes('rate limited') || errStr.includes('429')) {
            recordPollingFailureRef(429);
            setPortfolio(prev => ({
              ...prev,
              loading: false,
              isStale: true,
              error: 'Rate limited (will retry automatically)'
            }));
            return;
          }

          throw new Error(`Portfolio API error: ${portfolioData.error}`);
        }
        
        // Only update portfolio if we have valid data
        if (portfolioData.cash !== undefined && portfolioData.total_value !== undefined) {
          setPortfolio({
            cash: portfolioData.cash,
            total_value: portfolioData.total_value,
            available_margin: portfolioData.available_margin || 0,
            positions: portfolioData.positions || [],
            daily_pnl: portfolioData.daily_pnl || 0,
            total_pnl: portfolioData.total_pnl || 0,
            currency: portfolioData.currency || 'USD',
            paper_trading: portfolioData.paper_trading || false,
            loading: false,
            subscriptionRequired: false,
            apiKeyRequired: false,
            lastUpdated: Date.now(),
            isStale: false,
            note: portfolioData.note // Capture note from backend
          });
          console.log(`✅ Portfolio updated: ${portfolioData.cash} cash, ${portfolioData.total_value} total, currency: ${portfolioData.currency || 'USD'}, paper_trading: ${portfolioData.paper_trading || false}`);
          portfolioSuccess = true;
          
          // Fetch trading stats after portfolio loads
          fetchTradingStats();
        } else {
          throw new Error("Portfolio data missing required fields");
        }
      } catch (portfolioError) {
        console.log("❌ Portfolio fetch error:", portfolioError);
        
        // Check if user is admin - admins should bypass API key requirements
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const isAdmin = user.is_admin || user.role === 'super_admin' || user.admin_role === 'super_admin';
        
        if (isAdmin) {
          console.log("🔐 Admin user detected - bypassing portfolio API key requirements");
          // Set default portfolio for admin and continue
          // Admin users can have $0 balance, don't reset portfolio on error
          setPortfolio(prev => ({
            ...prev,
            loading: false,
            subscriptionRequired: false,
            apiKeyRequired: false
          }));
          // Continue with admin flow - don't return early
        } else {
          // Handle portfolio errors for regular users - check for subscription/API key issues
          if (portfolioError.response && portfolioError.response.status === 402) {
            console.log("🔒 User subscription required for portfolio access");
            subscriptionErrorHandled = true;
            // Set portfolio with subscription required state but preserve values
            setPortfolio(prev => ({
              ...prev,
              loading: false,
              subscriptionRequired: true  // Flag to show subscription prompt
            }));
            // DO NOT RETURN - Continue with free market data loading
          } else if (portfolioError.response && portfolioError.response.status === 500) {
            console.log("🔒 Server error likely due to missing API configuration - continuing with market data");
            subscriptionErrorHandled = true;
            
            // Check if it's specifically a Hyperliquid API key issue
            const errorMessage = portfolioError.response.data?.detail || "";
            if (errorMessage.includes("Hyperliquid API key not configured") || errorMessage.includes("wallet not configured")) {
              console.log("🔑 Hyperliquid API keys not configured - continuing with market data");
              // Preserve values but set flags
              setPortfolio(prev => ({
                ...prev,
                loading: false,
                subscriptionRequired: true,
                apiKeyRequired: true
              }));
            } else {
              // Other 500 errors - preserve values but set subscription flag
              setPortfolio(prev => ({
                ...prev,
                loading: false,
                subscriptionRequired: true
              }));
            }
            // DO NOT RETURN - Continue with free market data loading
          } else {
            // Re-throw other errors to be handled by outer catch block
            throw portfolioError;
          }
        }
      }
      
      // Fetch trading status with better error handling
      try {
        // Trading status is now fetched in portfolio section to optimize loading
        console.log("📊 Trading status will be fetched with portfolio data...");
        
        // Initial paint: fetch public trading status to avoid flicker
        // Increased timeout to 15s for slow backend startup
        try {
          const tradingResponse = await axios.get(`${BACKEND_URL}/api/trading-status`, { timeout: 15000 });
          const tradingData = tradingResponse.data;
          // If us_time is N/A, try to enrich from market-status
          if (tradingData?.market_status?.us_time === "N/A") {
            try {
              const marketResponse = await axios.get(`${BACKEND_URL}/api/market-status`, { timeout: 15000 });
              const marketData = marketResponse.data;
              tradingData.market_status.us_time = marketData.us_time;
              tradingData.market_status.market_hours = marketData.market_hours;
            } catch (e) { /* ignore market data fetch errors */ }
          }
          setTradingStatus(tradingData);
        } catch (e) {
          // Silently handle timeout errors - will retry on next poll
          if (e?.code !== 'ECONNABORTED') {
            console.warn('Trading status fetch error:', e?.message || e);
          }
        }

        
        
      } catch (tradingError) {
        console.warn("⚠️ Trading status fetch failed:", tradingError);
        
        // Don't overwrite trading status if we already have valid data
        // Only set fallback if tradingStatus is null (first load)
        if (!tradingStatus) {
          // Use calendar logic for market hours instead of defaulting to closed
          const now = new Date();
          const etTime = new Date(now.toLocaleString("en-US", {timeZone: "America/New_York"}));
          const hour = etTime.getHours();
          const minute = etTime.getMinutes();
          const currentTimeMinutes = hour * 60 + minute;
          const marketOpen = 9 * 60 + 30; // 9:30 AM
          const marketClose = 16 * 60; // 4:00 PM
          const isWeekday = etTime.getDay() >= 1 && etTime.getDay() <= 5;
          const isMarketHours = currentTimeMinutes >= marketOpen && currentTimeMinutes < marketClose;
          const shouldBeOpen = isWeekday && isMarketHours;
          
          console.log(`📅 Calendar fallback: ET=${etTime.toLocaleTimeString()}, shouldBeOpen=${shouldBeOpen}`);
          
          setTradingStatus({ 
            active: false, 
            market_status: {
              mode: shouldBeOpen ? "OPEN" : "CLOSED",
              strategy: "unknown",
              us_open: shouldBeOpen,
              us_time: etTime.toLocaleString("en-US", {
                timeZone: "America/New_York",
                hour12: true,
                timeZoneName: "short"
              }),
              market_hours: {
                is_open: shouldBeOpen
              }
            }
          });
        } else {
          console.log("⚠️ Keeping existing trading status due to fetch error");
        }
      }
      
      // Set other default states and fetch disaster recovery
      setTrades([]);
      // Do not reset technical analysis here; it is fetched independently via fetchTechnicalAnalysis
      
      // Disaster recovery removed
      
      // Success - set loading to false
      setLoading(false);
      
      // Check for PostHog errors and log them separately (they shouldn't affect core functionality)
      if (typeof window !== 'undefined' && window.posthog) {
        console.log("📊 PostHog analytics initialized (non-critical for app functionality)");
      }
      
    } catch (error) {
      console.error(`❌ Data fetch error (attempt ${retryCount + 1}):`, error);
      
      // Check if this is a PostHog-related error (non-critical)
      const isPosthogError = error.message && (
        error.message.includes('posthog') || 
        error.message.includes('WebSocket') ||
        error.message.includes('ws://') ||
        error.message.includes('wss://')
      );
      
      if (isPosthogError) {
        console.warn("⚠️ PostHog analytics error detected (non-critical) - continuing with app functionality");
        setLoading(false);
        return; // Don't retry for PostHog errors
      }
      
      // Retry loop disabled to avoid long blocking during login
      if (retryCount < maxRetries) {
        console.log(`🔄 Retrying in 2 seconds... (${retryCount + 1}/${maxRetries})`);
        await new Promise(resolve => setTimeout(resolve, 2000));
        return await fetchData(retryCount + 1, subscriptionErrorHandled); // Pass flag to next attempt
      } else {
        console.error("❌ All retry attempts failed. Analyzing what actually failed...");
        
        // Check if we handled a subscription error during any attempt
        if (subscriptionErrorHandled) {
          console.log("🔒 Subscription error was handled - not showing generic error");
          setLoading(false);
          return; // Exit without setting error - let subscription prompt show
        }
        
        // More specific error messages based on what actually failed
        let specificError = "Unable to connect to trading services.";
        
        if (error.code === 'NETWORK_ERROR' || error.message.includes('Network Error')) {
          specificError = "Network connection error. Please check your internet connection and try again.";
        } else if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
          specificError = "Connection timeout. The server is taking too long to respond. Please try again.";
        } else if (error.response?.status === 500) {
          specificError = "Server error. This may be due to missing API configuration. Please contact support if this persists.";
        } else if (error.response?.status === 401) {
          specificError = "Authentication failed. Please log out and log back in.";
        } else if (error.response?.status === 403) {
          specificError = "Access denied. Your account may not have the required permissions.";
        } else if (error.response?.status === 404) {
          specificError = "Requested resource not found. Please try again later.";
        }
        
        // Log final error but do NOT show global error screen; keep dashboard usable
        console.error("❌ Final dashboard load error:", specificError, error);
        setLoading(false);
      }
    }
  };

  const checkSystemHealthAndRestart = async () => {
    try {
      // Temporary: do not auto-restart services during live ingestion stabilization
      // We only run a light health check and never trigger restart from frontend.
      if (!isAuthenticated) return false;
      try {
        const latestStatsResp = await axios.get(`${API}/latest-stats`, { timeout: 8000 });
        const statsOk = latestStatsResp?.data?.status === 'success';
        if (statsOk) {
          console.log("✅ Health check OK (no frontend-triggered restart)");
        }
      } catch (_) { /* ignore */ }
      return false;
    } catch (error) {
      console.warn("⚠️ Health check failed:", error.message);
      return false;
    }
  };

  const loadInitialMarketData = async () => {
    console.log("📈 Loading initial market data (bulk-first)...");
    // Use liveSymbols for initial load if available
    const symbolsToLoad = (liveSymbols && liveSymbols.length > 0) ? liveSymbols : (activeSymbols && activeSymbols.length > 0 ? activeSymbols : getAllSymbolsFlat());
    try {
      const firstPage = symbolsToLoad.slice(0, PAGE_BATCH);
      await fetchMarketDataBulk(firstPage);
    } catch (e) {
      console.warn('Initial bulk warm-up failed:', e?.message || e);
    }
  };

  const fetchTrades = async () => {
    try {
      console.log("Fetching trades...");
      const response = await axios.get(`${API}/trades`, { timeout: 15000 }); // Increased for centralized system
      console.log("Trades received");
      if (response.data) {
        setTrades(response.data);
      }
    } catch (error) {
      console.error('Error fetching trades:', error);
      setTrades([]);
    }
  };

  const fetchMarketData = async (symbol) => {
    try {
      // Use single-symbol only as fallback or when bulk disabled
      if (USE_BULK_LATEST) return;
      console.log(`📊 Fetching market data for ${symbol}...`);
      const response = await axios.get(`${API}/latest/${symbol}`, { timeout: 8000 });
      if (response.data) {
        const cleanData = {
          ...response.data,
          price: parseFloat(response.data.price || 0),
          change: parseFloat(response.data.change || 0),
          change_percent: parseFloat(response.data.change_percent || 0),
          loading: false,
          error: false
        };
        if (cleanData.price > 1000000 || cleanData.price < 0) {
          cleanData.price = 0;
        }
        setMarketData(prev => ({ ...prev, [symbol]: cleanData }));
      }
    } catch (error) {
      setMarketData(prev => ({
        ...prev,
        [symbol]: { 
          ...(prev[symbol]||{}),
          loading: false, 
          error: true,
          status: 'error'
        }
      }));
    }
  };

  const fetchTechnicalAnalysis = async (symbol) => {
    const currentSymbol = symbol || selectedSymbol || 'BTC-USD';
    try {
      console.log(`📊 Fetching technical analysis for ${currentSymbol}...`);
      // guard to avoid multiple concurrent TA calls
      if (fetchTechnicalAnalysis._inFlight && fetchTechnicalAnalysis._inFlight[currentSymbol]) {
        return;
      }
      fetchTechnicalAnalysis._inFlight = fetchTechnicalAnalysis._inFlight || {};
      fetchTechnicalAnalysis._inFlight[currentSymbol] = true;
      // Don't show loading spinner for updates - keep UI stable
      // Only mark as loading on initial load with no data
      if (!technicalAnalysis || !technicalAnalysis.indicators) {
        setTechnicalAnalysisLoading(true);
      }
      
      const headers = {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
      };
      
      const response = await axios.get(`${API}/technical-analysis/${currentSymbol}`, { 
        headers,
        timeout: 15000 
      });
      console.log(`✅ Technical analysis for ${currentSymbol} loaded:`, response.data);
      
      if (response.data) {
        // Update data smoothly without re-rendering entire component
        setTechnicalAnalysis(prevTA => ({
          ...prevTA,
          ...response.data,
          symbol: currentSymbol
        }));
        lastTARef.current[currentSymbol] = response.data;
        console.log(`✅ Technical analysis state updated for ${currentSymbol}:`, response.data.signal, response.data.strength);
      } else {
        console.warn(`⚠️ No data in technical analysis response for ${currentSymbol}`);
      }
    } catch (error) {
      console.error(`❌ Error fetching technical analysis for ${currentSymbol}:`, error?.message || error);
      // Keep previous TA if available; only set a brief fallback once
      if (!technicalAnalysis || !technicalAnalysis.indicators) {
        setTechnicalAnalysis({
          symbol: currentSymbol,
          signal: 'HOLD',
          strength: 0,
          indicators: { rsi: 50, ema_short: 0, ema_long: 0, macd: 0, volume_ratio: 1, volatility: 0.02 },
          enhanced_metrics: null,
          reasons: ['TA temporarily unavailable']
        });
      }
    } finally {
      if (fetchTechnicalAnalysis._inFlight) delete fetchTechnicalAnalysis._inFlight[currentSymbol];
      // Only clear loading on initial load
      if (technicalAnalysisLoading) {
        setTechnicalAnalysisLoading(false);
      }
    }
  };

  const fetchTradingStatus = async () => {
    // Legacy trading-status subsystem disabled for this build.
    return;
  };



  // stopTrading removed — replaced by stopAutonomousTrading which calls grid-bot API

  const handleSymbolClick = async (symbol) => {
    // Only update selection; TA will be fetched by the effect watching selectedSymbol
    setSelectedSymbol(symbol);
  };

  // DISABLED: Technical analysis auto-refresh - panel removed
  // useEffect(() => {
  //   if (!isAuthenticated) return;
  //   if (!selectedSymbol) return;
  //   ... technical analysis polling disabled ...
  // }, [selectedSymbol, isAuthenticated]);

  // Persist risk level to localStorage removed - feature deprecated

  // Formatters imported from utils/formatters.js
  const formatCurrency = (amount) => formatCurrencyUtil(amount, portfolio?.currency);

  // formatPercentage, formatTimestamp, formatCooldownTimer, getSignalColor, getMarketStatusColor
  // imported from utils/formatters.js

  if (loading) {
    // Ensure background is painted immediately to avoid white flash in preview
    return (
      <div style={{minHeight:'100vh', backgroundColor:'#111827', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center'}}>
        <div style={{textAlign:'center'}}>
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-xl">Loading Trading Dashboard...</p>
          {isInIframe && (
            <p className="text-sm text-gray-400 mt-2">Preview Mode: For full experience, open in new tab</p>
          )}
        </div>
      </div>
    );
  }



  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-400 text-6xl mb-4">⚠️</div>
          <h2 className="text-xl mb-4 text-red-400">Error Loading Dashboard</h2>
          <p className="text-gray-400 mb-6">{error}</p>
          <button
            onClick={() => {
              setError(null);
              setLoading(true);
              fetchData();
            }}
            className="bg-blue-600 hover:bg-blue-700 px-6 py-2 rounded font-semibold"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Show authentication loading
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  // Show legal pages if requested
  if (showLegalPage) {
    const renderLegalPage = () => {
      switch (showLegalPage) {
        case 'terms':
          return <TermsOfService />;
        case 'privacy':
          return <PrivacyPolicy />;
        case 'risk':
          return <RiskDisclaimer />;
        case 'cookies':
          return <CookiePolicy />;
        case 'support':
          return <ContactSupport onBack={() => { setShowLegalPage(null); window.history.pushState({}, '', '/'); }} />;
        case 'support-privacy':
          return <ContactSupport onBack={() => { setShowLegalPage(null); window.history.pushState({}, '', '/'); }} initialType="privacy" />;
        case 'support-legal':
          return <ContactSupport onBack={() => { setShowLegalPage(null); window.history.pushState({}, '', '/'); }} initialType="legal" />;
        default:
          return null;
      }
    };

    return (
      <div>
        {renderLegalPage()}
        <LegalFooter />
      </div>
    );
  }

  // Show email verification page if active
  if (showEmailVerification) {
    return <EmailVerificationPage onLogin={handleLogin} />;
  }

  // Show password reset page if active
  if (showPasswordReset) {
    return (
      <ResetPasswordPage 
        token={resetToken}
        onBack={handleResetBack}
        onResetComplete={(message) => {
          setResetMessage(message);
          setShowPasswordReset(false);
        }}
      />
    );
  }

  // Show login page if user clicked to login from landing page
  if (!isAuthenticated && showLogin) {
    return (
      <LoginPage 
        onLogin={handleLogin} 
        supportEmail="support@tradingbot.ai"
        onForgotPassword={() => setShowPasswordReset(true)}
        onBack={() => setShowLogin(false)}
        initialLoginMode={initialLoginMode}
      />
    );
  }

  // Show "How to Start" page if active (accessible to all)
  if (showHowToStart) {
    return (
      <HowToStartPage 
        onBack={() => setShowHowToStart(false)}
        isAuthenticated={isAuthenticated}
      />
    );
  }

  // Show landing page if not authenticated and not specifically showing login
  if (!isAuthenticated) {
    return (
      <div>
        <AnnouncementBanner currentPage="home" />
        <LandingPage 
          onSignUp={() => {
            setShowLogin(true);
            setInitialLoginMode(false); // Set to signup mode
          }}
          onLogin={() => {
            setShowLogin(true);
            setInitialLoginMode(true); // Set to login mode
          }}
          setShowHowToStart={setShowHowToStart}
          isInIframe={isInIframe}
        />
        <LegalFooter />
      </div>
    );
  }

  // Show subscription page if subscription is active
  if (showSubscription) {
    return (
      <SubscriptionPage 
        onBack={() => setShowSubscription(false)}
        userToken={localStorage.getItem('token')}
      />
    );
  }
  // How to Start page logic moved to top to support unauthenticated access

  // Show settings page if settings is active
  if (showSettings) {
    return (
      <SettingsPage 
        onBack={() => {
          setShowSettings(false);
          // Refresh subscription status when returning from settings
          checkSubscriptionStatus();
        }}
        userToken={localStorage.getItem('token')}
        onKeysUpdated={() => {
          setSubscriptionStatus(prev => prev ? { ...prev, has_api_key: true } : prev);
        }}
        onModeChange={() => {
          // Force refresh portfolio when trading mode changes (bypass cache)
          refreshPortfolioData(true);
        }}
      />
    );
  }

  // Show main dashboard if authenticated
  if (isAuthenticated) {
    // Show admin dashboard FIRST (before subscription loading check)
    // CRITICAL FIX: Ensure AdminDashboard is mounted and not immediately unmounted
    // FORCE ADMIN VIEW: If user is admin, they MUST see AdminDashboard. No access to user dashboard.
    const isUserAdmin = currentUser?.is_admin || currentUser?.role === 'super_admin' || currentUser?.admin_role === 'super_admin';
    
    if (isUserAdmin) {
      return (
        <AdminDashboard 
          onLogout={handleLogout}
        />
      );
    }
    
    // Show loading screen while checking subscription (for non-admin users only)
    if (subscriptionLoading) {
      return (
        <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500 mx-auto"></div>
            <h2 className="text-xl mt-4">Loading Dashboard...</h2>
            <p className="text-gray-400 mt-2">Checking subscription status...</p>
          </div>
        </div>
      );
    }

    // Show main dashboard for all authenticated users (subscribed or not)
    const isCooldownActive = cooldownRemainingSec > 0;
    const isRegimeFilterPause = !isCooldownActive
      && autonomousStatus.user_participating
      && autonomousStatus.regime_filter_pause_active;
    const autonomousDisplayStatus = isCooldownActive
      ? 'PAUSED'
      : (isRegimeFilterPause
        ? `PAUSED - ${String(autonomousStatus.regime_filter_disallowed_regime || 'REGIME').toUpperCase()}`
        : (autonomousStatus.user_participating ? 'RUNNING' : 'STOPPED'));

    return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <DashboardHeader tradingStatus={tradingStatus} currentTime={currentTime} />

      {/* Backend Health Check Banner */}
      <HealthCheckBanner 
        show={showHealthBanner} 
        message={healthCheckMessage} 
        onRetry={() => checkBackendHealth()} 
      />



      {/* Announcement Banner */}
      <AnnouncementBanner currentPage="dashboard" />

      {/* Error Message */}
      <ErrorToast message={error} onClose={() => setError(null)} />
      
      {/* Action Error Message (non-blocking) */}
      <ErrorToast message={actionError} onClose={() => setActionError(null)} />

      {/* Success Message */}
      <SuccessToast message={successMessage} onClose={() => setSuccessMessage(null)} />

      {/* Live Mode Stop Confirmation Modal */}
      {showStopConfirm && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-gray-800 border border-red-500 rounded-lg p-6 max-w-md mx-4 shadow-2xl">
            <div className="flex items-center mb-4">
              <span className="text-3xl mr-3">⚠️</span>
              <h3 className="text-xl font-bold text-red-400">Stop Live Trading?</h3>
            </div>
            <div className="text-gray-300 mb-6 space-y-3">
              <p>This will <span className="text-red-400 font-semibold">SELL ALL</span> your open positions on Hyperliquid at current market price.</p>
              <p className="text-sm text-gray-400">Your positions will be closed immediately. This action cannot be undone.</p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => setShowStopConfirm(false)}
                className="flex-1 px-4 py-3 bg-gray-600 hover:bg-gray-500 rounded-lg font-semibold transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => stopAutonomousTrading(true)}
                className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-700 rounded-lg font-semibold transition-colors"
              >
                Yes, Stop & Sell All
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Close All Positions Confirmation Modal */}
      <ConfirmModal
        isOpen={showCloseAllConfirm}
        onClose={() => setShowCloseAllConfirm(false)}
        onConfirm={executeCloseAllPositions}
        title="Close All Positions & Orders?"
        message="This will immediately close ALL your open positions AND cancel ALL open orders on Hyperliquid.

• LONG positions will be sold at market prices
• SHORT positions will be bought back at market prices
• All pending limit orders will be cancelled
• The Grid Bot will be stopped if running
• This action cannot be undone"
        confirmText={closingPositions ? "Closing..." : "Yes, Close All"}
        cancelText="Cancel"
        variant="danger"
      />

      {/* Sync Positions Confirmation Modal */}
      <ConfirmModal
        isOpen={showSyncConfirm}
        onClose={() => setShowSyncConfirm(false)}
        onConfirm={executeSyncPositions}
        title="Sync with Active Traders?"
        message="This will immediately open ALL positions currently held by active traders at CURRENT market prices.

• Bypasses the 'new trades only' safety check
• Positions will match the traders' allocation
• Ensure you have sufficient funds"
        confirmText={syncingPositions ? "Syncing..." : "Yes, Sync Now"}
        cancelText="Cancel"
        variant="warning"
      />

      <div className="max-w-7xl mx-auto p-4 md:p-6">
        {/* User Navigation */}
        <DashboardNavigation
          subscriptionStatus={subscriptionStatus}
          portfolio={portfolio}
          setShowSubscription={setShowSubscription}
          setShowHowToStart={setShowHowToStart}
          setShowSettings={setShowSettings}
          handleLogout={handleLogout}
        />

        {/* Portfolio Summary - Using Component */}
        <PortfolioSummary portfolio={portfolio} formatCurrency={formatCurrency} />

        {/* Control Panel */}
        <div className="mb-8">
          <ControlPanel
            autonomousStatus={autonomousStatus}
            isCooldownActive={isCooldownActive}
            isRegimeFilterPause={isRegimeFilterPause}
            autonomousDisplayStatus={autonomousDisplayStatus}
            cooldownRemainingSec={cooldownRemainingSec}
            subscriptionStatus={subscriptionStatus}
            portfolio={portfolio}
            pollingBackoffUi={pollingBackoffUi}
            startAutonomousTrading={startAutonomousTrading}
            stopAutonomousTrading={stopAutonomousTrading}
            closeAllPositions={closeAllPositions}
            closingPositions={closingPositions}
            autonomousLoading={autonomousLoading}
            tradingNotional={tradingNotional}
            tradingLeverage={tradingLeverage}
            setTradingNotional={setTradingNotional}
            setTradingLeverage={setTradingLeverage}
            tradingCoin={tradingCoin}
            setTradingCoin={setTradingCoin}
            autoRotateCoin={autoRotateCoin}
            autoRotateLoading={autoRotateLoading}
            toggleAutoRotateCoin={toggleAutoRotateCoin}
            cutAndTrail={cutAndTrail}
            cutAndTrailLoading={cutAndTrailLoading}
            toggleCutAndTrail={toggleCutAndTrail}
            ctAdaptive={ctAdaptive}
            ctAdaptiveLoading={ctAdaptiveLoading}
            toggleCtAdaptive={toggleCtAdaptive}
            hybridExit={hybridExit}
            hybridExitLoading={hybridExitLoading}
            toggleHybridExit={toggleHybridExit}
            supportedCoins={supportedCoins}
            maxLeverageByCoin={maxLeverageByCoin}
            allowedTradeRegimes={allowedTradeRegimes}
            allowedTradeRegimesLoading={allowedTradeRegimesLoading}
            toggleAllowedTradeRegime={toggleAllowedTradeRegime}
            lastStopReason={lastStopReason}
            onDismissStopReason={() => setLastStopReason(null)}
            liveBotRegime={liveBotRegime}
            liveBotCoin={liveBotCoin}
            BACKEND_URL={BACKEND_URL}
          />
        </div>

        {/* Grid Bot Dashboard - Full Width (Primary Trading Interface) */}
        <div className="mb-6">
          <GridBotDashboard onStatusChange={handleGridBotStatusChange} />
        </div>

        {/* Current Positions */}
        <PositionsTable
          portfolio={portfolio}
          formatCurrency={formatCurrency}
          formatPercentage={formatPercentage}
          closePosition={closePosition}
          closingCoin={closingCoin}
          autonomousStatus={autonomousStatus}
        />

      </div>


      {/* Autonomous Trading Control Section - Admin Only */}
      <AdminTradingPanel
        currentUser={currentUser}
        autonomousStatus={autonomousStatus}
        autonomousLoading={autonomousLoading}
        startAutonomousTrading={startAutonomousTrading}
        stopAutonomousTrading={stopAutonomousTrading}
        emergencyStopTrading={emergencyStopTrading}
        fetchAutonomousStatus={fetchAutonomousStatus}
      />
      
      {/* Legal Footer */}
      <LegalFooter />
      
      {/* Cookies Notification - always show when needed */}
      <CookiesNotification />
    </div>
  );
};
}

export default TradingBot;